import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ApprovalsSection from '../components/ApprovalsSection';
import TeachersSection from '../components/TeachersSection';
import StudentsSection from '../components/StudentsSection';
import SchedulesSection from '../components/SchedulesSection';
import AnnouncementsManager from '../components/AnnouncementsManager';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import ChatPanel from '../components/ChatPanel';
import AdminCurriculumProgression from '../components/AdminCurriculumProgression';
import OlympiadBuilder from '../components/OlympiadBuilder';
import { useNavigate } from 'react-router-dom';
import { Users, BookOpen, DollarSign, Calendar, TrendingUp, LogOut, Shield, UserPlus, CreditCard, Trash2, GraduationCap, Briefcase, Heart, Package, Plus, Video, Home, Settings, CheckCircle, XCircle, Clock, CalendarDays, Filter, KeyRound, Eye, EyeOff, Copy, Edit, Save, X, RefreshCw, FileText, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, ExternalLink, AlertTriangle, MessageSquare, Check, Menu, BarChart3, PieChart, Activity, Award, Zap, Target, RotateCcw, Code, Cpu, Calculator, FlaskConical, Languages, Mic, Mail, Folder, Receipt, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';
import { CodeITTLogo } from '../components/CodeITTLogo';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import EnrollmentManager from '../components/EnrollmentManager';
import AdminOverview from '../components/AdminOverview';
import AdminTeacherDetailsSidebar from '../components/AdminTeacherDetailsSidebar';
import AdminEarningsOverview from '../components/AdminEarningsOverview';
import LeaderboardSidebar from '../components/LeaderboardSidebar';
import TeacherLoadWidget from '../components/TeacherLoadWidget';
import NotificationBell from '../components/NotificationBell';
import MyFilesPage from '../components/MyFilesPage';
import ReceiptApprovalsPanel from '../components/ReceiptApprovalsPanel';
import DigestAdminPanel from '../components/DigestAdminPanel';
import StudentProfileCard from '../components/StudentProfileCard';
import TeacherPayDashboard from '../components/TeacherPayDashboard';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [adminMobileSidebar, setAdminMobileSidebar] = useState(false);
  const [curriculumMgrOpen, setCurriculumMgrOpen] = useState(false);
  const [olympiadBuilderOpen, setOlympiadBuilderOpen] = useState(false);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [profitStats, setProfitStats] = useState(null);
  const [demosData, setDemosData] = useState({ demos: [], stats: {} });
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Filter states
  const [teacherTimeFilter, setTeacherTimeFilter] = useState('month');
  const [demoStatusFilter, setDemoStatusFilter] = useState('all');
  const [demoDateFilter, setDemoDateFilter] = useState('');
  
  // Dialog states
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createStudentOpen, setCreateStudentOpen] = useState(false);
  const [createDemoOpen, setCreateDemoOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', username: '', password: '', role: 'student', parent_email: '' });
  const [newStudent, setNewStudent] = useState({ student_name: '', student_username: '', student_password: '', grade: '', country: '', state: '', timezone: 'IST', verticals: [], program_name: '', contact_number: '', alternate_phone: '', student_email: '', parent_name: '', parent_email: '', parent_phone: '' });
  const [newDemo, setNewDemo] = useState({ name: '', email: '', phone: '', timezone: 'UTC', scheduled_date: '', notes: '' });
  const [isCreating, setIsCreating] = useState(false);
  
  // Student profile editing
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', grade: '', region: '', timezone: '', verticals: [], contact_number: '', student_email: '', parent_name: '', parent_email: '', parent_phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  const VERTICALS = ['Coding', 'Mathematics', 'English', 'Chess', 'Robotics', 'Graphic Design', 'Science', 'AI', 'IELTS', 'Spoken Mastery'];

  const COUNTRIES = [
    { code: 'IN', name: 'India', flag: '\ud83c\uddee\ud83c\uddf3' },
    { code: 'US', name: 'United States', flag: '\ud83c\uddfa\ud83c\uddf8' },
    { code: 'CA', name: 'Canada', flag: '\ud83c\udde8\ud83c\udde6' },
    { code: 'GB', name: 'United Kingdom', flag: '\ud83c\uddec\ud83c\udde7' },
    { code: 'AE', name: 'UAE', flag: '\ud83c\udde6\ud83c\uddea' },
    { code: 'SG', name: 'Singapore', flag: '\ud83c\uddf8\ud83c\uddec' },
    { code: 'AU', name: 'Australia', flag: '\ud83c\udde6\ud83c\uddfa' },
    { code: 'DE', name: 'Germany', flag: '\ud83c\udde9\ud83c\uddea' },
    { code: 'SA', name: 'Saudi Arabia', flag: '\ud83c\uddf8\ud83c\udde6' },
    { code: 'QA', name: 'Qatar', flag: '\ud83c\uddf6\ud83c\udde6' },
    { code: 'KW', name: 'Kuwait', flag: '\ud83c\uddf0\ud83c\uddfc' },
    { code: 'OM', name: 'Oman', flag: '\ud83c\uddf4\ud83c\uddf2' },
    { code: 'OTHER', name: 'Other', flag: '\ud83c\udf0d' },
  ];

  const STATE_MAP = {
    'IN': ['Andhra Pradesh', 'Karnataka', 'Tamil Nadu', 'Maharashtra', 'Telangana', 'Kerala', 'Delhi', 'Gujarat', 'Rajasthan', 'Uttar Pradesh', 'West Bengal', 'Other'],
    'US': ['California', 'Texas', 'New York', 'Florida', 'Illinois', 'New Jersey', 'Washington', 'Massachusetts', 'Virginia', 'Other'],
    'CA': ['Ontario', 'British Columbia', 'Alberta', 'Quebec', 'Manitoba', 'Other'],
    'GB': ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Other'],
    'AE': ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Other'],
  };

  // Curriculum templates
  const [curriculumTemplates, setCurriculumTemplates] = useState([]);
  // Curriculum state
  const [currVerticals, setCurrVerticals] = useState([]);
  const [currSelectedVertical, setCurrSelectedVertical] = useState(null);
  const [currSelectedProgram, setCurrSelectedProgram] = useState(null);
  const [currSelectedSubject, setCurrSelectedSubject] = useState(null);
  const [currModules, setCurrModules] = useState([]);
  const [currExpandedModule, setCurrExpandedModule] = useState(null);
  const [currExpandedTopic, setCurrExpandedTopic] = useState(null);
  const [currAddModuleOpen, setCurrAddModuleOpen] = useState(false);
  const [currNewModuleName, setCurrNewModuleName] = useState('');
  const [currAddTopicOpen, setCurrAddTopicOpen] = useState(null);
  const [currNewTopic, setCurrNewTopic] = useState({ name: '', proficiency: 'Beginner', type: 'Concept', created_by_teacher: '', description: '', project_link: '' });
  const [currEditTopic, setCurrEditTopic] = useState(null);
  const [currAddProjectOpen, setCurrAddProjectOpen] = useState(null);
  const [currNewProject, setCurrNewProject] = useState({ name: '', description: '', project_link: '' });
  const [docUploadOpen, setDocUploadOpen] = useState(null); // topic_id when open
  const [docUploadLabel, setDocUploadLabel] = useState('');
  const [docUploadDesc, setDocUploadDesc] = useState('');
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [topicDocsMap, setTopicDocsMap] = useState({}); // {topic_id: [docs]}
  const [currEditProject, setCurrEditProject] = useState(null);
  const [currAddVerticalOpen, setCurrAddVerticalOpen] = useState(false);
  const [currNewVertical, setCurrNewVertical] = useState({ name: '', description: '' });
  const [currAddProgramOpen, setCurrAddProgramOpen] = useState(false);
  const [currNewProgram, setCurrNewProgram] = useState({ name: '', age_range: '' });
  const [currAddSubjectOpen, setCurrAddSubjectOpen] = useState(false);
  const [currNewSubjectName, setCurrNewSubjectName] = useState('');

  // Teacher Payout state
  const [teacherPayouts, setTeacherPayouts] = useState([]);
  // Monthly payouts view (from Google Sheet, gid=1608561437)
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const buildMonthOptions = () => {
    const opts = [];
    const now = new Date();
    // Start February 2026 per requirement
    const startY = 2026, startM = 1; // 0-indexed February
    const curY = now.getFullYear(), curM = now.getMonth();
    let y = startY, m = startM;
    while (y < curY || (y === curY && m <= curM)) {
      opts.push({ month: MONTH_NAMES[m], year: String(y), label: `${MONTH_NAMES[m]} ${y}` });
      m += 1; if (m > 11) { m = 0; y += 1; }
    }
    return opts.reverse(); // newest first
  };
  const monthOptions = buildMonthOptions();
  const defaultOpt = monthOptions[0] || { month: 'February', year: '2026', label: 'February 2026' };
  const [monthlyPayoutSel, setMonthlyPayoutSel] = useState(`${defaultOpt.month}|${defaultOpt.year}`);
  const [monthlyPayoutData, setMonthlyPayoutData] = useState(null);
  const [monthlyPayoutLoading, setMonthlyPayoutLoading] = useState(false);
  const fetchMonthlyPayouts = async () => {
    const [month, year] = monthlyPayoutSel.split('|');
    if (!month || !year) return;
    setMonthlyPayoutLoading(true);
    try {
      const res = await api.get(`/admin/teacher-payouts/monthly?month=${encodeURIComponent(month)}&year=${encodeURIComponent(year)}`);
      setMonthlyPayoutData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not fetch monthly payouts');
      setMonthlyPayoutData({ rows: [], count: 0, total_credited: 0 });
    } finally {
      setMonthlyPayoutLoading(false);
    }
  };
  useEffect(() => { if (activeSection === 'teacher-payouts') fetchMonthlyPayouts(); }, [monthlyPayoutSel, activeSection]);
  const [addPayoutOpen, setAddPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ teacher_id: '', amount: 0, currency: 'INR', classes_count: 0, period: '', payment_method: 'Bank Transfer', notes: '' });

  // Manual Revenue state
  const [manualRevenues, setManualRevenues] = useState([]);
  const [addRevenueOpen, setAddRevenueOpen] = useState(false);
  const [revenueForm, setRevenueForm] = useState({ student_name: '', date_of_transaction: '', vertical: '', classes_added: 0, amount: 0, currency: 'INR', payment_method: '', notes: '' });
  const [importingCurriculum, setImportingCurriculum] = useState(false);

  // Teacher profile view state
  const [viewTeacherProfile, setViewTeacherProfile] = useState(null);
  const [editTeacher, setEditTeacher] = useState(null);
  const [addTeacherOpen, setAddTeacherOpen] = useState(false);
  const [newTeacherForm, setNewTeacherForm] = useState({ name: '', email: '', vertical: '' });

  // Class Log state
  const [classLogOpen, setClassLogOpen] = useState(false);
  const [classLogData, setClassLogData] = useState(null);
  const [classLogLoading, setClassLogLoading] = useState(false);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [txEditForm, setTxEditForm] = useState({});

  // Demo management
  const [demoSettings, setDemoSettings] = useState({ teachers: [], round_robin: {} });
  const [demoClasses, setDemoClasses] = useState([]);
  const [newDemoAssign, setNewDemoAssign] = useState({ date: '', time: '', student_name: '', teacher_id: '', auto_assign: true });
  const [assigningDemo, setAssigningDemo] = useState(false);
  
  // Schedule states
  const [schedules, setSchedules] = useState([]);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [createScheduleOpen, setCreateScheduleOpen] = useState(false);
  const [scheduleType, setScheduleType] = useState('recurring');
  const [newSchedule, setNewSchedule] = useState({ student_id: '', teacher_id: '', days_of_week: [], time: '16:00', timezone: 'IST', date: '', });
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Approvals
  const [approvalData, setApprovalData] = useState({ leave_requests: [], reschedule_requests: [], pending_count: 0 });
  const [approvalsTab, setApprovalsTab] = useState('leave');
  const [teacherChangeReqs, setTeacherChangeReqs] = useState([]);
  const [teacherReadinessMap, setTeacherReadinessMap] = useState({}); // {teacher_id: readiness}

  // Teacher Issues
  const [teacherIssues, setTeacherIssues] = useState([]);

  // Analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(true);
  const [resettingData, setResettingData] = useState(false);

  const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleResetData = async () => {
    if (!confirm('WARNING: This will permanently delete ALL students, teachers, and parents. Passwords and curriculum will be kept. Continue?')) return;
    setResettingData(true);
    try {
      const res = await api.post('/admin/reset-data');
      toast.success(`Reset complete: ${res.data.deleted.students} students, ${res.data.deleted.teachers} teachers, ${res.data.deleted.parents} parents removed`);
      fetchAllData();
    } catch (e) { toast.error('Reset failed'); }
    setResettingData(false);
  };

  const TIMEZONE_MAP = {
    'IST': 'Asia/Kolkata',
    'EST': 'America/New_York',
    'PST': 'America/Los_Angeles',
    'GMT': 'Europe/London',
    'SGT': 'Asia/Singapore',
  };

  const getCurrentTimeInTZ = (tz) => {
    try {
      const ianaZone = TIMEZONE_MAP[tz] || tz;
      return new Date().toLocaleTimeString('en-US', { timeZone: ianaZone, hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return ''; }
  };

  // Student management dialog
  const [manageStudentOpen, setManageStudentOpen] = useState(false);
  const [managingStudent, setManagingStudent] = useState(null);
  const [profileCardStudentId, setProfileCardStudentId] = useState(null);
  const [studentMappings, setStudentMappings] = useState({ assigned_teacher_id: '', assigned_courses: [], assigned_modules: [] });
  const [savingMapping, setSavingMapping] = useState(false);
  const [classAllocation, setClassAllocation] = useState(0);
  const [classStats, setClassStats] = useState(null);
  const [renewalData, setRenewalData] = useState({ classes_to_add: 0, discount: 0, payment_received: 0, currency: 'INR', payment_method: 'razorpay_upi' });
  const [showRenewal, setShowRenewal] = useState(false);
  const USD_TO_INR = 83;

  // Student profile edit
  const [studentProfileEdit, setStudentProfileEdit] = useState({ name: '', grade: '', region: '', timezone: '', contact_number: '', student_email: '', parent_name: '', parent_email: '', parent_phone: '', verticals: [] });
  const [savingStudentProfile, setSavingStudentProfile] = useState(false);

  // Revenue edit
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [editRevenueData, setEditRevenueData] = useState({});
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceTab, setShowInvoiceTab] = useState(false);

  // Class Packages
  const [classPackages, setClassPackages] = useState([]);
  const [createPackageOpen, setCreatePackageOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({ name: '', num_classes: 0, price_inr: 0, price_usd: 0, description: '', is_active: true });
  const [savingPackage, setSavingPackage] = useState(false);

  // Purchase Requests
  const [purchaseRequests, setPurchaseRequests] = useState([]);

  // Assign Package to Student (Mark as Paid)
  const [assignPackageOpen, setAssignPackageOpen] = useState(false);
  const [assignData, setAssignData] = useState({ student_id: '', package_id: '', payment_method: 'razorpay_upi', currency: 'INR', payment_received: 0 });
  const [assigningPackage, setAssigningPackage] = useState(false);

  // Define fetchAllData BEFORE the useEffect that uses it
  const fetchAllData = useCallback(async () => {
    try {
      const [dashboardRes, teachersRes, studentsRes, usersRes, coursesRes, packagesRes, transactionsRes, revenueRes, demosRes, credentialsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/teachers/detailed'),
        api.get('/admin/students/detailed'),
        api.get('/admin/users'),
        api.get('/admin/courses'),
        api.get('/admin/packages'),
        api.get('/admin/all-transactions'),
        api.get('/admin/revenue/stats'),
        api.get('/admin/demos'),
        api.get('/admin/credentials')
      ]);
      
      setDashboardData(dashboardRes.data);
      setTeachers(teachersRes.data);
      setStudents(studentsRes.data);
      setAllUsers(usersRes.data);
      setAllCourses(coursesRes.data);
      setAllPackages(packagesRes.data);
      setAllTransactions(transactionsRes.data);
      setRevenueStats(revenueRes.data);
      setDemosData(demosRes.data);
      setCredentials(credentialsRes.data);
      // Fetch analytics
      try { const aRes = await api.get('/admin/analytics'); setAnalyticsData(aRes.data); } catch {}
      // Fetch profit stats
      try { const profitRes = await api.get('/admin/profit-stats'); setProfitStats(profitRes.data); } catch {}
      // Fetch approvals and issues for overview widgets
      try { const apRes = await api.get('/admin/approvals'); setApprovalData(apRes.data); } catch {}
      try { const tiRes = await api.get('/admin/teacher-issues'); setTeacherIssues(tiRes.data); } catch {}
    } catch (error) {
      
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScheduleData = useCallback(async () => {
    try {
      const [sRes, cRes, rRes] = await Promise.all([
        api.get('/admin/schedules'),
        api.get('/admin/scheduled-classes'),
        api.get('/admin/reschedule-requests')
      ]);
      setSchedules(sRes.data);
      setScheduledClasses(cRes.data);
      setRescheduleRequests(rRes.data);
    } catch (err) {  }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchAllData();
  }, [user, navigate, fetchAllData]);

  useEffect(() => { if (activeSection === 'schedules') fetchScheduleData(); }, [activeSection, fetchScheduleData]);

  const handleCreateSchedule = async () => {
    setSavingSchedule(true);
    try {
      if (scheduleType === 'recurring') {
        if (!newSchedule.student_id || !newSchedule.teacher_id || newSchedule.days_of_week.length === 0) { toast.error('Fill all required fields'); setSavingSchedule(false); return; }
        await api.post('/admin/schedules/recurring', newSchedule);
        toast.success('Recurring schedule created!');
      } else {
        if (!newSchedule.student_id || !newSchedule.teacher_id || !newSchedule.date) { toast.error('Fill all required fields'); setSavingSchedule(false); return; }
        await api.post('/admin/schedules/one-time', { student_id: newSchedule.student_id, teacher_id: newSchedule.teacher_id, date: newSchedule.date, time: newSchedule.time, timezone: newSchedule.timezone });
        toast.success('One-time class scheduled!');
      }
      setCreateScheduleOpen(false);
      setNewSchedule({ student_id: '', teacher_id: '', days_of_week: [], time: '16:00', timezone: 'IST', date: '' });
      fetchScheduleData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create schedule'); }
    setSavingSchedule(false);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Delete this schedule? Future classes will be cancelled.')) return;
    try { await api.delete(`/admin/schedules/${scheduleId}`); toast.success('Schedule deleted'); fetchScheduleData(); } catch { toast.error('Failed'); }
  };

  const handleRescheduleAction = async (reqId, action) => {
    try { await api.put(`/admin/reschedule-requests/${reqId}`, { action }); toast.success(`Request ${action}d`); fetchScheduleData(); } catch { toast.error('Failed'); }
  };

  const handleMarkComplete = async (classId) => {
    try { await api.put(`/admin/scheduled-classes/${classId}/complete`); toast.success('Marked complete'); fetchScheduleData(); } catch { toast.error('Failed'); }
  };

  const toggleDay = (day) => {
    setNewSchedule(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  // --- Package & Purchase handlers ---
  const fetchPackagesData = async () => {
    try {
      const [pkgRes, prRes] = await Promise.all([
        api.get('/admin/class-packages'),
        api.get('/admin/purchase-requests')
      ]);
      setClassPackages(pkgRes.data);
      setPurchaseRequests(prRes.data);
    } catch (err) {  }
  };

  useEffect(() => { if (activeSection === 'packages') fetchPackagesData(); }, [activeSection]);
  useEffect(() => { if (activeSection === 'curriculum') { fetchCurrVerticals(); setCurrSelectedVertical(null); setCurrSelectedProgram(null); setCurrSelectedSubject(null); } }, [activeSection]);
  useEffect(() => { if (activeSection === 'teacher-payouts') fetchTeacherPayouts(); }, [activeSection]);
  useEffect(() => { if (activeSection === 'revenue') fetchManualRevenues(); }, [activeSection]);
  useEffect(() => {
    if (activeSection === 'approvals') { fetchApprovals(); fetchTeacherChangeReqs(); }
  }, [activeSection]);
  useEffect(() => { if (activeSection === 'teacher-issues') fetchTeacherIssues(); }, [activeSection]);
  useEffect(() => { if (activeSection === 'transactions') fetchTransactions(); }, [activeSection]);

  const fetchClassLog = async (studentId) => {
    setClassLogLoading(true);
    try {
      const res = await api.get(`/admin/student-class-log/${studentId}`);
      setClassLogData(res.data);
      setClassLogOpen(true);
    } catch { toast.error('Failed to load class log'); }
    setClassLogLoading(false);
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get('/admin/transactions');
      setTransactions(res.data);
    } catch { toast.error('Failed to load transactions'); }
  };

  const handleEditTransaction = async (tx) => {
    try {
      if (tx.source === 'manual_revenue') {
        await api.put(`/admin/manual-revenues/${tx.id}`, txEditForm);
      } else if (tx.source === 'teacher_payout') {
        await api.put(`/admin/teacher-payouts/${tx.id}`, txEditForm);
      } else if (tx.source === 'teacher_deduction') {
        await api.put(`/admin/teacher-deductions/${tx.id}`, txEditForm);
      }
      toast.success('Transaction updated');
      setEditingTransaction(null);
      fetchTransactions();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteTransaction = async (tx) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      if (tx.source === 'manual_revenue') {
        await api.delete(`/admin/manual-revenues/${tx.id}`);
      } else if (tx.source === 'teacher_payout') {
        await api.delete(`/admin/teacher-payouts/${tx.id}`);
      } else if (tx.source === 'teacher_deduction') {
        await api.delete(`/admin/teacher-deductions/${tx.id}`);
      }
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchTeacherIssues = async () => {
    try { const res = await api.get('/admin/teacher-issues'); setTeacherIssues(res.data); } catch (err) {  }
  };

  const handleResolveIssue = async (issueId) => {
    try {
      await api.put(`/admin/teacher-issues/${issueId}`, { status: 'resolved' });
      toast.success('Issue marked as resolved');
      fetchTeacherIssues();
    } catch { toast.error('Failed to update issue'); }
  };

  const fetchApprovals = async () => {
    try { const res = await api.get('/admin/approvals'); setApprovalData(res.data); } catch (err) {  }
  };

  const handleLeaveAction = async (reqId, action) => {
    try {
      await api.put(`/admin/leave-requests/${reqId}`, { action });
      toast.success(`Leave request ${action}`);
      fetchApprovals();
    } catch { toast.error('Failed'); }
  };

  const handleApprovalRescheduleAction = async (reqId, action) => {
    try {
      await api.put(`/admin/reschedule-requests/${reqId}/action`, { action });
      toast.success(`Request ${action}`);
      fetchApprovals();
    } catch { toast.error('Failed'); }
  };

  const fetchTeacherChangeReqs = async () => {
    try { const res = await api.get('/admin/teacher-change-requests'); setTeacherChangeReqs(res.data); } catch {}
  };
  const fetchTeacherReadiness = async (teacherId) => {
    try {
      const res = await api.get(`/admin/teachers/${teacherId}/readiness`);
      setTeacherReadinessMap(m => ({ ...m, [teacherId]: res.data }));
    } catch {}
  };
  const handleSetTeacherActive = async (teacherId, isActive) => {
    try {
      await api.put(`/admin/teachers/${teacherId}/set-active`, { is_active: isActive });
      toast.success(isActive ? 'Teacher activated for live classes' : 'Teacher deactivated');
      fetchTeacherReadiness(teacherId);
      fetchTeachers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleApproveTeacherChange = async (reqId, newTeacherId) => {
    try {
      await api.post(`/admin/teacher-change-requests/${reqId}/approve`, { new_teacher_id: newTeacherId || '' });
      toast.success('Teacher change approved');
      fetchTeacherChangeReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleRejectTeacherChange = async (reqId) => {
    const note = prompt('Reject reason (optional):', '') || '';
    try {
      await api.post(`/admin/teacher-change-requests/${reqId}/reject`, { note });
      toast.success('Request rejected');
      fetchTeacherChangeReqs();
    } catch { toast.error('Failed'); }
  };

  const fetchCurrVerticals = async () => {
    try { const res = await api.get('/admin/curriculum-verticals'); setCurrVerticals(res.data); } catch {}
  };
  const fetchCurrModules = async (subject) => {
    try { const res = await api.get(`/admin/curriculum-subject/${subject}`); setCurrModules(res.data); } catch {}
  };
  const handleSelectSubject = (subject) => { setCurrSelectedSubject(subject); setCurrExpandedModule(null); setCurrExpandedTopic(null); fetchCurrModules(subject); };
  const handleAddModule = async () => {
    if (!currNewModuleName.trim() || !currSelectedSubject) return;
    try { await api.post('/admin/curriculum-templates/module', { vertical: currSelectedVertical?.name || 'Coding', program: currSelectedProgram?.name || '', subject: currSelectedSubject, module_name: currNewModuleName }); setCurrNewModuleName(''); setCurrAddModuleOpen(false); fetchCurrModules(currSelectedSubject); fetchCurrVerticals(); toast.success('Module added'); } catch { toast.error('Failed'); }
  };
  const handleDeleteModule = async (id) => {
    if (!confirm('Delete this module and all its topics?')) return;
    try { await api.delete(`/admin/curriculum-templates/module/${id}`); fetchCurrModules(currSelectedSubject); fetchCurrVerticals(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };
  const handleAddTopic = async (moduleId) => {
    if (!currNewTopic.name.trim()) return;
    try { await api.post(`/admin/curriculum-templates/module/${moduleId}/topic`, currNewTopic); setCurrNewTopic({ name: '', proficiency: 'Beginner', type: 'Concept', created_by_teacher: '', description: '', project_link: '' }); setCurrAddTopicOpen(null); fetchCurrModules(currSelectedSubject); toast.success('Topic added'); } catch { toast.error('Failed'); }
  };
  const handleUpdateTopic = async (moduleId, topicId, data) => {
    try { await api.put(`/admin/curriculum-templates/module/${moduleId}/topic/${topicId}`, data); fetchCurrModules(currSelectedSubject); toast.success('Updated'); setCurrEditTopic(null); } catch { toast.error('Failed'); }
  };
  const handleDeleteTopic = async (moduleId, topicId) => {
    if (!confirm('Delete this topic and all its projects?')) return;
    try { await api.delete(`/admin/curriculum-templates/module/${moduleId}/topic/${topicId}`); fetchCurrModules(currSelectedSubject); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };
  // Vertical CRUD
  const handleAddVertical = async () => {
    if (!currNewVertical.name.trim()) return;
    try { await api.post('/admin/curriculum-verticals', currNewVertical); setCurrNewVertical({ name: '', description: '' }); setCurrAddVerticalOpen(false); fetchCurrVerticals(); toast.success('Vertical added'); } catch { toast.error('Failed'); }
  };
  const handleDeleteVertical = async (vId) => {
    if (!confirm('Delete this vertical and all its programs?')) return;
    try { await api.delete(`/admin/curriculum-verticals/${vId}`); fetchCurrVerticals(); setCurrSelectedVertical(null); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };
  // Program CRUD
  const handleAddProgram = async () => {
    if (!currNewProgram.name.trim() || !currSelectedVertical) return;
    try { await api.post(`/admin/curriculum-verticals/${currSelectedVertical.id}/program`, currNewProgram); setCurrNewProgram({ name: '', age_range: '' }); setCurrAddProgramOpen(false); fetchCurrVerticals(); toast.success('Program added'); } catch { toast.error('Failed'); }
  };
  const handleDeleteProgram = async (progId) => {
    if (!confirm('Delete this program?')) return;
    try { await api.delete(`/admin/curriculum-verticals/${currSelectedVertical.id}/program/${progId}`); fetchCurrVerticals(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };
  // Subject CRUD
  const handleAddSubject = async () => {
    if (!currNewSubjectName.trim() || !currSelectedProgram) return;
    try { await api.post(`/admin/curriculum-verticals/${currSelectedVertical.id}/program/${currSelectedProgram.id}/subject`, { name: currNewSubjectName }); setCurrNewSubjectName(''); setCurrAddSubjectOpen(false); fetchCurrVerticals(); toast.success('Subject added'); } catch { toast.error('Failed'); }
  };
  const handleDeleteSubject = async (subjName) => {
    if (!confirm(`Delete subject "${subjName}"?`)) return;
    try { await api.delete(`/admin/curriculum-verticals/${currSelectedVertical.id}/program/${currSelectedProgram.id}/subject/${encodeURIComponent(subjName)}`); fetchCurrVerticals(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };
  // Project CRUD
  const handleAddProject = async (moduleId, topicId) => {
    if (!currNewProject.name.trim()) return;
    try { await api.post(`/admin/curriculum-templates/module/${moduleId}/topic/${topicId}/project`, currNewProject); setCurrNewProject({ name: '', description: '', project_link: '' }); setCurrAddProjectOpen(null); fetchCurrModules(currSelectedSubject); toast.success('Project added'); } catch { toast.error('Failed'); }
  };
  const handleUpdateProject = async (moduleId, topicId, projectId, data) => {
    try { await api.put(`/admin/curriculum-templates/module/${moduleId}/topic/${topicId}/project/${projectId}`, data); fetchCurrModules(currSelectedSubject); setCurrEditProject(null); toast.success('Updated'); } catch { toast.error('Failed'); }
  };
  const handleDeleteProject = async (moduleId, topicId, projectId) => {
    try { await api.delete(`/admin/curriculum-templates/module/${moduleId}/topic/${topicId}/project/${projectId}`); fetchCurrModules(currSelectedSubject); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  // ===== Topic Documents (Admin upload/edit/delete) =====
  const fetchTopicDocs = async (templateId, topicId) => {
    try {
      const res = await api.get(`/curriculum-template/${templateId}/topic/${topicId}/documents`);
      setTopicDocsMap(m => ({ ...m, [topicId]: res.data }));
    } catch { /* topic may have no documents — that's fine */ }
  };
  const handleUploadDoc = async (templateId, topicId) => {
    if (!docUploadFile) { toast.error('Please pick a file'); return; }
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', docUploadFile);
      fd.append('label', docUploadLabel);
      fd.append('description', docUploadDesc);
      await api.post(`/curriculum-template/${templateId}/topic/${topicId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setDocUploadOpen(null); setDocUploadLabel(''); setDocUploadDesc(''); setDocUploadFile(null);
      fetchTopicDocs(templateId, topicId);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    setDocUploading(false);
  };
  const handleEditDoc = async (templateId, topicId, docId, current) => {
    const label = prompt('Label:', current.label || '');
    if (label === null) return;
    const description = prompt('Description:', current.description || '');
    if (description === null) return;
    try {
      await api.put(`/curriculum-template/${templateId}/topic/${topicId}/documents/${docId}`, { label, description });
      toast.success('Document updated');
      fetchTopicDocs(templateId, topicId);
    } catch { toast.error('Update failed'); }
  };
  const handleDeleteDoc = async (templateId, topicId, docId) => {
    if (!window.confirm('Delete this document permanently?')) return;
    try {
      await api.delete(`/curriculum-template/${templateId}/topic/${topicId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchTopicDocs(templateId, topicId);
    } catch { toast.error('Delete failed'); }
  };

  const fetchTeacherPayouts = async () => {
    try { const res = await api.get('/admin/teacher-payouts'); setTeacherPayouts(res.data); } catch {}
  };
  const [deductionForm, setDeductionForm] = useState({ teacher_id: '', amount: 0, reason: '', notes: '' });
  const [addDeductionOpen, setAddDeductionOpen] = useState(false);
  const [payoutDetailTeacher, setPayoutDetailTeacher] = useState(null);
  const handleAddPayout = async () => {
    if (!payoutForm.teacher_id || payoutForm.amount <= 0) { toast.error('Select teacher and enter amount'); return; }
    try { await api.post('/admin/teacher-payouts', payoutForm); setAddPayoutOpen(false); setPayoutForm({ teacher_id: '', amount: 0, currency: 'INR', period: '', payment_method: 'Bank Transfer', notes: '' }); fetchTeacherPayouts(); toast.success('Payout added'); } catch { toast.error('Failed'); }
  };
  const handleAddDeduction = async () => {
    if (!deductionForm.teacher_id || deductionForm.amount <= 0) { toast.error('Select teacher and enter amount'); return; }
    try { await api.post('/admin/teacher-deductions', deductionForm); setAddDeductionOpen(false); setDeductionForm({ teacher_id: '', amount: 0, reason: '', notes: '' }); fetchTeacherPayouts(); toast.success('Deduction added'); } catch { toast.error('Failed'); }
  };
  const fetchManualRevenues = async () => {
    try { const res = await api.get('/admin/manual-revenues'); setManualRevenues(res.data); } catch {}
  };
  const fetchInvoices = async () => {
    try { const res = await api.get('/invoices'); setInvoices(res.data); } catch {}
  };
  useEffect(() => { if (activeSection === 'revenue' && showInvoiceTab) fetchInvoices(); }, [activeSection, showInvoiceTab]);
  const handleAddRevenue = async () => {
    if (!revenueForm.student_name || revenueForm.amount <= 0) { toast.error('Fill required fields'); return; }
    try { await api.post('/admin/add-revenue', revenueForm); setAddRevenueOpen(false); setRevenueForm({ student_name: '', date_of_transaction: '', vertical: '', classes_added: 0, amount: 0, currency: 'INR', payment_method: '', notes: '' }); fetchManualRevenues(); toast.success('Revenue added'); } catch { toast.error('Failed'); }
  };
  const handleGenerateInvoice = async (tx) => {
    try {
      const res = await api.post('/invoices/generate', { payment_id: tx.id, student_id: tx.student_id, student_name: tx.student_name, payment_received: tx.amount_inr || tx.amount || tx.payment_received, currency: tx.currency || 'INR', num_classes: tx.classes_added || tx.num_classes || 0, payment_method: tx.payment_method || '', transaction_ref: tx.transaction_ref || tx.id });
      toast.success(`Invoice ${res.data.invoice_number} generated!`);
      fetchInvoices();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to generate invoice'); }
  };
  const handleDownloadInvoicePdf = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `invoice-${invoiceId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };
  useEffect(() => { if (activeSection === 'demos') fetchDemoData(); }, [activeSection]);

  const handleSavePackage = async () => {
    if (!packageForm.name || packageForm.num_classes <= 0) {
      toast.error('Package name and number of classes are required'); return;
    }
    if (packageForm.price_inr <= 0 && packageForm.price_usd <= 0) {
      toast.error('Enter amount in either INR or USD'); return;
    }
    setSavingPackage(true);
    try {
      if (editingPackage) {
        await api.put(`/admin/class-packages/${editingPackage.id}`, packageForm);
        toast.success('Package updated!');
      } else {
        await api.post('/admin/class-packages', packageForm);
        toast.success('Package created!');
      }
      setCreatePackageOpen(false);
      setEditingPackage(null);
      setPackageForm({ name: '', num_classes: 0, price_inr: 0, price_usd: 0, description: '', is_active: true });
      fetchPackagesData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingPackage(false);
  };

  const handleDeletePackage = async (id) => {
    if (!confirm('Delete this package?')) return;
    try { await api.delete(`/admin/class-packages/${id}`); toast.success('Deleted'); fetchPackagesData(); } catch { toast.error('Failed'); }
  };

  const openEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({ name: pkg.name, num_classes: pkg.num_classes, price_inr: pkg.price_inr, price_usd: pkg.price_usd, description: pkg.description || '', is_active: pkg.is_active });
    setCreatePackageOpen(true);
  };

  const handlePurchaseAction = async (reqId, action) => {
    try {
      await api.put(`/admin/purchase-requests/${reqId}`, { action });
      toast.success(`Purchase ${action}ed`);
      fetchPackagesData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAssignPackage = async () => {
    if (!assignData.student_id || !assignData.package_id) {
      toast.error('Select student and package'); return;
    }
    setAssigningPackage(true);
    try {
      await api.post('/admin/assign-package', assignData);
      toast.success('Package assigned & marked as paid!');
      setAssignPackageOpen(false);
      setAssignData({ student_id: '', package_id: '', payment_method: 'razorpay_upi', currency: 'INR', payment_received: 0 });
      fetchPackagesData();
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setAssigningPackage(false);
  };

  const getAssignPrice = () => {
    const pkg = classPackages.find(p => p.id === assignData.package_id);
    if (!pkg) return 0;
    return assignData.currency === 'INR' ? pkg.price_inr : pkg.price_usd;
  };

  const PAYMENT_METHOD_LABELS = {
    razorpay_card: 'Razorpay (Card)',
    razorpay_upi: 'Razorpay (UPI)',
    paypal: 'PayPal',
    bank_transfer: 'Bank Transfer',
  };

  const handleImpersonate = async (userId, userName, role) => {
    try {
      const res = await api.post(`/admin/impersonate/${userId}`);
      const { access_token, user } = res.data;
      // Store current admin session
      const adminToken = localStorage.getItem('token');
      const adminUser = localStorage.getItem('user');
      localStorage.setItem('admin_backup_token', adminToken);
      localStorage.setItem('admin_backup_user', adminUser);
      // Switch to impersonated user
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('impersonating', 'true');
      toast.success(`Now viewing as ${userName} (${role})`);
      window.location.href = role === 'teacher' ? '/teacher/dashboard' : role === 'parent' ? '/parent/dashboard' : '/student/dashboard';
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to impersonate'); }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCreateTeacher = async () => {
    if (!newTeacherForm.name || !newTeacherForm.email) { toast.error('Name and email required'); return; }
    try {
      const res = await api.post('/admin/create-teacher', newTeacherForm);
      toast.success(`${res.data.message} | Password: ${res.data.default_password}`);
      setAddTeacherOpen(false);
      setNewTeacherForm({ name: '', email: '', vertical: '' });
      const teachersRes = await api.get('/admin/teachers/detailed');
      setTeachers(teachersRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create teacher'); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await api.post('/admin/create-user', newUser);
      toast.success(`${newUser.role.charAt(0).toUpperCase() + newUser.role.slice(1)} created successfully!`);
      setCreateUserOpen(false);
      setNewUser({ name: '', email: '', username: '', password: '', role: 'student', parent_email: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.student_name || !newStudent.student_username) {
      toast.error('Student name and username are required'); return;
    }
    setIsCreating(true);
    try {
      await api.post('/admin/create-student-with-parent', newStudent);
      toast.success(`Student ${newStudent.student_name} created with parent account!`);
      setCreateStudentOpen(false);
      setNewStudent({ student_name: '', student_username: '', student_password: '', grade: '', country: '', state: '', timezone: 'IST', verticals: [], program_name: '', contact_number: '', alternate_phone: '', student_email: '', parent_name: '', parent_email: '', parent_phone: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create student');
    } finally {
      setIsCreating(false);
    }
  };

  const toggleStudentVertical = (v) => {
    setNewStudent(prev => ({
      ...prev,
      verticals: prev.verticals.includes(v) ? prev.verticals.filter(x => x !== v) : [...prev.verticals, v]
    }));
  };

  const toggleProfileVertical = (v) => {
    setProfileForm(prev => ({
      ...prev,
      verticals: prev.verticals.includes(v) ? prev.verticals.filter(x => x !== v) : [...prev.verticals, v]
    }));
  };

  const openEditProfile = async (student) => {
    // Normalize verticals — imported students have object entries; profile form uses string list.
    const normalizeVerticals = (arr) => (arr || []).map(v => typeof v === 'string' ? v : (v?.vertical || '')).filter(Boolean);
    try {
      const res = await api.get(`/admin/students/${student.id}/profile`);
      const s = res.data;
      setEditingProfile(s);
      setProfileForm({
        name: s.name || '',
        grade: s.grade || '',
        region: s.region || '',
        timezone: s.timezone || '',
        verticals: normalizeVerticals(s.verticals),
        contact_number: s.contact_number || '',
        student_email: s.student_email || '',
        parent_name: s.parent_name || '',
        parent_email: s.parent_email_address || '',
        parent_phone: s.parent_phone || '',
      });
    } catch {
      setEditingProfile(student);
      setProfileForm({
        name: student.name || '',
        grade: student.grade || '',
        region: student.region || '',
        timezone: student.timezone || '',
        verticals: normalizeVerticals(student.verticals),
        contact_number: student.contact_number || '',
        student_email: student.student_email || '',
        parent_name: student.parent_name || '',
        parent_email: student.parent_email_address || '',
        parent_phone: student.parent_phone || '',
      });
    }
  };

  const handleSaveProfile = async () => {
    if (!editingProfile) return;
    setSavingProfile(true);
    try {
      await api.put(`/admin/students/${editingProfile.id}/profile`, profileForm);
      toast.success('Student profile updated!');
      setEditingProfile(null);
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    }
    setSavingProfile(false);
  };

  // Curriculum handlers
  const fetchCurriculumTemplates = async () => {
    try { const res = await api.get('/admin/curriculum-templates'); setCurriculumTemplates(res.data); } catch {}
  };
  const handleImportCurriculum = async () => {
    setImportingCurriculum(true);
    try {
      const res = await api.post('/admin/import-curriculum');
      toast.success(res.data.message);
      fetchCurriculumTemplates();
    } catch { toast.error('Failed to import curriculum'); }
    setImportingCurriculum(false);
  };

  // Demo handlers
  const fetchDemoData = async () => {
    try { const [settingsRes, classesRes] = await Promise.all([api.get('/admin/demo-settings'), api.get('/admin/demo-classes')]); setDemoSettings(settingsRes.data); setDemoClasses(classesRes.data); } catch {}
  };
  const handleToggleDemo = async (teacherId, currentVal) => {
    try {
      await api.put(`/admin/teachers/${teacherId}/demo-toggle`, { demo_eligible: !currentVal });
      toast.success(`Demo ${!currentVal ? 'enabled' : 'disabled'}`);
      fetchDemoData();
    } catch { toast.error('Failed to toggle'); }
  };
  const handleAssignDemo = async () => {
    if (!newDemoAssign.date || !newDemoAssign.time) { toast.error('Date and time required'); return; }
    setAssigningDemo(true);
    try {
      const res = await api.post('/admin/assign-demo', newDemoAssign);
      toast.success(res.data.message);
      setNewDemoAssign({ date: '', time: '', student_name: '', teacher_id: '', auto_assign: true });
      fetchDemoData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    setAssigningDemo(false);
  };

  const handleCreateDemo = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await api.post('/admin/demos', newDemo);
      toast.success('Demo booking created!');
      setCreateDemoOpen(false);
      setNewDemo({ name: '', email: '', phone: '', timezone: 'UTC', scheduled_date: '', notes: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create demo');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateDemoStatus = async (demoId, status) => {
    try {
      await api.patch(`/admin/demos/${demoId}`, { status });
      toast.success('Demo status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete ${userName}?`)) return;
    try {
      await api.delete(`/admin/delete-user/${userId}`);
      toast.success('User deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getTeacherStats = (teacher, period) => {
    switch (period) {
      case 'day':
        return { classes: teacher.classes_today || 0, present: teacher.present_today || 0, absent: teacher.absent_today || 0, incomplete: teacher.incomplete_today || 0 };
      case 'week':
        return { classes: teacher.classes_week || 0, present: teacher.present_week || 0, absent: teacher.absent_week || 0, incomplete: teacher.incomplete_week || 0 };
      case 'month':
      default:
        return { classes: teacher.classes_month || 0, present: teacher.present_month || 0, absent: teacher.absent_month || 0, incomplete: teacher.incomplete_month || 0 };
    }
  };

  const filteredDemos = useMemo(() => demoStatusFilter === 'all' 
    ? demosData.demos 
    : demosData.demos.filter(d => d.status === demoStatusFilter), [demoStatusFilter, demosData.demos]);

  const AVAILABLE_MODULES = [
    'Scratch - Module 1: Beginner',
    'Scratch - Module 2: Intermediate',
    'Scratch - Module 3: Advanced',
    'Thunkable', 'Game Development',
    'Python', 'Java', 'HTML & CSS', 'App Development', 'Py Game',
    'GenAI'
  ];

  // Modules & Courses grouped per vertical — now fetched from MongoDB via
  // /api/vertical-modules so admins can manage them without a code change.
  // The `DEFAULT_MODULES_BY_VERTICAL` (backend) mirrors the previous hardcode
  // so first-load parity is guaranteed while the collection seeds.
  const [modulesByVertical, setModulesByVertical] = useState({});
  const [moduleCatalogItems, setModuleCatalogItems] = useState([]);
  const fetchVerticalModules = useCallback(async () => {
    try {
      const res = await api.get('/vertical-modules');
      setModulesByVertical(res.data?.modules_by_vertical || {});
      setModuleCatalogItems(res.data?.items || []);
    } catch (e) { /* keep empty */ }
  }, []);
  useEffect(() => { fetchVerticalModules(); }, [fetchVerticalModules]);
  const modulesForVertical = (v) => modulesByVertical[v] || AVAILABLE_MODULES;

  const [mappingVertical, setMappingVertical] = useState('');

  const openStudentManagement = async (student) => {
    setManagingStudent(student);
    setShowRenewal(false);
    // Preserve object-shape verticals (with class counts + teacher) for the picker
    const normalizeVerticals = (arr) => (arr || []).map(v => {
      if (typeof v === 'string') return { vertical: v, classes_purchased: 0, classes_consumed: 0, status: 'Active' };
      return v?.vertical ? v : null;
    }).filter(Boolean);
    const verticalsList = normalizeVerticals(student.verticals);
    setMappingVertical(student.active_vertical || verticalsList[0]?.vertical || '');
    setStudentProfileEdit({
      name: student.name || '', grade: student.grade || '', region: student.region || '',
      timezone: student.timezone || 'IST', contact_number: student.contact_number || '',
      student_email: student.student_email || student.email || '',
      parent_name: student.parent_name || '', parent_email: student.parent_email || '',
      parent_phone: student.parent_phone || '',
      verticals: normalizeVerticals(student.verticals),
    });
    try {
      const res = await api.get(`/admin/students/${student.id}/mappings`);
      setStudentMappings({
        assigned_teacher_id: res.data.assigned_teacher_id || '',
        assigned_courses: (res.data.assigned_courses || []).map(c => c.id),
        assigned_modules: res.data.assigned_modules || [],
      });
    } catch {
      setStudentMappings({
        assigned_teacher_id: student.mapped_teacher_id || '',
        assigned_courses: [],
        assigned_modules: [],
      });
    }
    try {
      const statsRes = await api.get(`/admin/students/${student.id}/class-stats`);
      setClassStats(statsRes.data);
      setClassAllocation(statsRes.data.total_assigned || student.total_classes_assigned || 0);
    } catch {
      setClassStats(null);
      setClassAllocation(student.total_classes_assigned || 0);
    }
    setManageStudentOpen(true);
  };

  const handleSaveTeacher = async () => {
    if (!studentMappings.assigned_teacher_id) { toast.error('Select a teacher'); return; }
    setSavingMapping(true);
    try {
      const res = await api.put(`/admin/students/${managingStudent.id}/assign-teacher`, { teacher_id: studentMappings.assigned_teacher_id });
      toast.success(res.data.message);
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingMapping(false);
  };

  const handleSaveCourses = async () => {
    setSavingMapping(true);
    try {
      const res = await api.put(`/admin/students/${managingStudent.id}/assign-courses`, { course_ids: studentMappings.assigned_courses });
      toast.success(res.data.message);
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingMapping(false);
  };

  const handleSaveModules = async () => {
    setSavingMapping(true);
    try {
      const res = await api.put(`/admin/students/${managingStudent.id}/assign-modules`, { modules: studentMappings.assigned_modules });
      toast.success(res.data.message);
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingMapping(false);
  };

  const handleSaveStudentProfile = async () => {
    setSavingStudentProfile(true);
    try {
      await api.put(`/admin/students/${managingStudent.id}/profile`, studentProfileEdit);
      toast.success('Profile updated');
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingStudentProfile(false);
  };

  const handleDeleteRevenue = async (id) => {
    if (!window.confirm('Delete this revenue entry?')) return;
    try { await api.delete(`/admin/manual-revenues/${id}`); toast.success('Deleted'); fetchManualRevenues(); } catch { toast.error('Failed'); }
  };

  const handleSaveRevenueEdit = async () => {
    if (!editingRevenue) return;
    try { await api.put(`/admin/manual-revenues/${editingRevenue}`, editRevenueData); toast.success('Updated'); setEditingRevenue(null); fetchManualRevenues(); } catch { toast.error('Failed'); }
  };

  const toggleModule = (mod) => {
    setStudentMappings(prev => ({
      ...prev,
      assigned_modules: prev.assigned_modules.includes(mod)
        ? prev.assigned_modules.filter(m => m !== mod)
        : [...prev.assigned_modules, mod]
    }));
  };

  const toggleCourse = (courseId) => {
    setStudentMappings(prev => ({
      ...prev,
      assigned_courses: prev.assigned_courses.includes(courseId)
        ? prev.assigned_courses.filter(c => c !== courseId)
        : [...prev.assigned_courses, courseId]
    }));
  };

  const handleSaveClasses = async () => {
    setSavingMapping(true);
    try {
      await api.put(`/admin/students/${managingStudent.id}/assign-classes`, { total_classes: classAllocation });
      toast.success(`${classAllocation} classes assigned`);
      fetchAllData();
      const statsRes = await api.get(`/admin/students/${managingStudent.id}/class-stats`);
      setClassStats(statsRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingMapping(false);
  };

  const handleRenewal = async () => {
    if (renewalData.classes_to_add <= 0 || renewalData.payment_received <= 0) {
      toast.error('Please fill in classes and payment'); return;
    }
    setSavingMapping(true);
    try {
      const res = await api.post(`/admin/students/${managingStudent.id}/renew`, renewalData);
      toast.success(`Renewed! ${res.data.classes_added} classes added. New total: ${res.data.new_total}`);
      setShowRenewal(false);
      setRenewalData({ classes_to_add: 0, discount: 0, payment_received: 0, currency: 'INR', payment_method: 'razorpay_upi' });
      fetchAllData();
      const statsRes = await api.get(`/admin/students/${managingStudent.id}/class-stats`);
      setClassStats(statsRes.data);
      setClassAllocation(statsRes.data.total_assigned);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingMapping(false);
  };

  const renewalTotalINR = renewalData.currency === 'INR'
    ? renewalData.payment_received - renewalData.discount
    : (renewalData.payment_received * USD_TO_INR) - renewalData.discount;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-500/10">Loading...</div>;
  }

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'enrollments', label: 'Enrollments', icon: Package },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'profit', label: 'Profit', icon: TrendingUp },
    { id: 'demos', label: 'Demos', icon: CalendarDays },
    { id: 'schedules', label: 'Schedules', icon: Calendar },
    { id: 'transactions', label: 'Transactions', icon: CreditCard },
    { id: 'announcements', label: 'Announcements', icon: MessageSquare },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];
  const adminGroupItems = [
    { id: 'add-user', label: 'Add User', icon: UserPlus },
    { id: 'teachers', label: 'Teachers', icon: Briefcase },
    { id: 'teacher-details', label: 'Teacher Details', icon: FileText },
    { id: 'students', label: 'Students', icon: GraduationCap },
    { id: 'curriculum', label: 'Curriculum', icon: FileText },
    { id: 'teacher-issues', label: 'Teacher Issues', icon: AlertTriangle },
    { id: 'approvals', label: 'Approvals', icon: CheckCircle },
    { id: 'teacher-earnings', label: 'Teacher Earnings', icon: DollarSign },
    { id: 'teacher-pay', label: 'Teacher Pay', icon: Wallet },
    { id: 'teacher-payouts', label: 'Teacher Payouts', icon: CreditCard },
    { id: 'receipts', label: 'Receipt Approvals', icon: Receipt },
    { id: 'passwords', label: 'Passwords', icon: KeyRound },
    { id: 'digest', label: 'Weekly Digest', icon: Mail },
    { id: 'my-files', label: 'Files & Media', icon: Folder },
    { id: 'tools', label: 'Admin Tools', icon: Settings },
  ];
  const allNavItems = [...sidebarItems, ...adminGroupItems];

  return (
    <div className="min-h-screen flex" style={{ background: '#F8FAFC' }}>
      {/* Mobile sidebar overlay */}
      {adminMobileSidebar && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setAdminMobileSidebar(false)} />}

      {/* Left Sidebar */}
      <aside className={`w-64 glass-sidebar flex flex-col fixed h-full z-50 transition-transform duration-300 ${adminMobileSidebar ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-6 border-b border-slate-200 bg-white">
          <div className="flex items-center space-x-3">
            <CodeITTLogo size="large" />
            <div>
              <h1 className="text-lg font-bold text-[#0F172A]">CodeITT</h1>
              <p className="text-xs text-[#3B82F6] font-semibold">Admin Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setAdminMobileSidebar(false); }}
              className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl transition-all font-semibold text-xs
                ${activeSection === item.id 
                  ? 'bg-[#3B82F6] text-white shadow-lg shadow-blue-500/25' 
                  : 'text-[#334155] hover:bg-[#F0F7FF] hover:text-[#1E40AF]'
                }`}
              data-testid={`sidebar-${item.id}`}
            >
              <item.icon className={`w-3.5 h-3.5 ${activeSection === item.id ? 'text-white' : 'text-[#3B82F6]'}`} />
              <span>{item.label}</span>
            </button>
          ))}

          {/* Admin Group Dropdown */}
          <div className="pt-2">
            <button
              onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all font-semibold text-xs ${adminGroupItems.some(i => i.id === activeSection) ? 'bg-[#F0F7FF] text-[#1E40AF]' : 'text-[#334155] hover:bg-[#F0F7FF]'}`}
              data-testid="sidebar-admin-group"
            >
              <div className="flex items-center space-x-2.5">
                <Shield className={`w-3.5 h-3.5 ${adminGroupItems.some(i => i.id === activeSection) ? 'text-[#1E40AF]' : 'text-[#3B82F6]'}`} />
                <span>Admin</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${adminDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {adminDropdownOpen && (
              <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-[#3B82F6]/20 pl-3">
                {adminGroupItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveSection(item.id); setAdminMobileSidebar(false); }}
                    className={`w-full flex items-center space-x-2 px-2.5 py-1.5 rounded-lg transition-all text-xs
                      ${activeSection === item.id 
                        ? 'bg-[#3B82F6] text-white shadow-md' 
                        : 'text-[#334155] hover:bg-[#F0F7FF] hover:text-[#1E40AF]'
                      }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <item.icon className={`w-3.5 h-3.5 ${activeSection === item.id ? 'text-white' : 'text-[#3B82F6]'}`} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F0F7FF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0F172A] truncate">{user?.name}</p>
              <p className="text-xs text-[#64748B] font-semibold truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        <AnnouncementsBanner />
        {/* Top Bar with Logout */}
        <div className="sticky top-0 z-10 glass-topbar px-4 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setAdminMobileSidebar(true)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" data-testid="admin-mobile-menu">
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-slate-800">{allNavItems.find(i => i.id === activeSection)?.label || 'Dashboard'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell role="admin" />
            <Button className="bg-red-500 hover:bg-red-600 text-white font-bold shadow-sm" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
        <div className="p-3 sm:p-6 lg:p-8 admin-content">
        {/* Overview Section */}
        {activeSection === 'overview' && dashboardData && (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            {/* LEFT: Leaderboard sidebar (1/4 on xl) */}
            <div className="xl:col-span-1 order-2 xl:order-1">
              <LeaderboardSidebar mode="full" variant="left" />
            </div>
            {/* MAIN: Overview (3/4 on xl) */}
            <div className="xl:col-span-3 order-1 xl:order-2 space-y-4">
              <AdminOverview
                user={user} dashboardData={dashboardData} analyticsData={analyticsData}
                teachers={teachers} students={students} teacherIssues={teacherIssues}
                approvalData={approvalData} resettingData={resettingData}
                handleResetData={handleResetData} setActiveSection={setActiveSection}
                createUserOpen={createUserOpen} setCreateUserOpen={setCreateUserOpen}
                handleCreateUser={handleCreateUser} newUser={newUser} setNewUser={setNewUser}
                isCreating={isCreating}
              />
              <TeacherLoadWidget />
            </div>
          </div>
        )}



        {/* Enrollments Section */}
        {activeSection === 'enrollments' && (
          <div className="p-3 sm:p-6 max-w-6xl">
            <EnrollmentManager />
          </div>
        )}

        {activeSection === 'teacher-details' && (
          <AdminTeacherDetailsSidebar />
        )}

        {activeSection === 'teacher-earnings' && (
          <AdminEarningsOverview />
        )}

        {activeSection === 'teacher-pay' && (
          <TeacherPayDashboard />
        )}

        {/* Add User Section (from Admin dropdown) */}
        {activeSection === 'add-user' && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-slate-900">Add New User</h1>
              <p className="text-slate-500 font-semibold">Create teacher, student, or parent accounts</p>
            </div>
            <div className="nb-card p-6">
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label className="text-slate-900 font-bold">Full Name</Label>
                  <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required className="mt-1 border-2 border-slate-200 bg-slate-50 text-slate-900 focus:border-blue-400 placeholder:text-slate-500" placeholder="Enter full name" data-testid="add-user-name" />
                </div>
                <div>
                  <Label className="text-slate-900 font-bold">Email</Label>
                  <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required className="mt-1 border-2 border-slate-200 bg-slate-50 text-slate-900 focus:border-blue-400 placeholder:text-slate-500" placeholder="Enter email" data-testid="add-user-email" />
                </div>
                <div>
                  <Label className="text-slate-900 font-bold">Password</Label>
                  <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required className="mt-1 border-2 border-slate-200 bg-slate-50 text-slate-900 focus:border-blue-400 placeholder:text-slate-500" placeholder="Enter password" data-testid="add-user-password" />
                </div>
                <div>
                  <Label className="text-slate-900 font-bold">Role</Label>
                  <Select value={newUser.role} onValueChange={(val) => setNewUser({...newUser, role: val})}>
                    <SelectTrigger className="mt-1 border-2 border-slate-200 bg-slate-50 text-slate-900" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" disabled={isCreating} data-testid="add-user-submit">
                  {isCreating ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Teachers Section */}
        {activeSection === 'teachers' && (
          <TeachersSection
            teachers={teachers}
            teacherTimeFilter={teacherTimeFilter}
            setTeacherTimeFilter={setTeacherTimeFilter}
            setAddTeacherOpen={setAddTeacherOpen}
            getTeacherStats={getTeacherStats}
            teacherReadinessMap={teacherReadinessMap}
            fetchTeacherReadiness={fetchTeacherReadiness}
            handleSetTeacherActive={handleSetTeacherActive}
            handleImpersonate={handleImpersonate}
            setEditTeacher={setEditTeacher}
            setViewTeacherProfile={setViewTeacherProfile}
          />
        )}

        {/* Add Teacher Dialog */}
        <Dialog open={addTeacherOpen} onOpenChange={setAddTeacherOpen}>
          <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 text-xl">Add New Teacher</DialogTitle>
              <DialogDescription className="text-slate-500">Teacher ID will be auto-generated. Password = FirstName@123</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-slate-700 font-semibold">Full Name *</Label>
                <Input value={newTeacherForm.name} onChange={e => setNewTeacherForm({...newTeacherForm, name: e.target.value})} placeholder="e.g. Priya Sharma" data-testid="new-teacher-name" />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Email *</Label>
                <Input value={newTeacherForm.email} onChange={e => setNewTeacherForm({...newTeacherForm, email: e.target.value})} placeholder="e.g. priya@codeittacademy.com" data-testid="new-teacher-email" />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Vertical</Label>
                <Select value={newTeacherForm.vertical} onValueChange={v => setNewTeacherForm({...newTeacherForm, vertical: v})}>
                  <SelectTrigger data-testid="new-teacher-vertical"><SelectValue placeholder="Select vertical" /></SelectTrigger>
                  <SelectContent>
                    {VERTICALS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" onClick={handleCreateTeacher} data-testid="submit-create-teacher">
                Create Teacher
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Teacher Profile Dialog */}
        <Dialog open={!!viewTeacherProfile} onOpenChange={(open) => { if (!open) setViewTeacherProfile(null); }}>
          <DialogContent className="bg-[#0f1b2e] border border-slate-200 text-slate-900 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-slate-900 text-xl">Teacher Profile</DialogTitle>
            </DialogHeader>
            {viewTeacherProfile && (
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#3B82F6]/20 flex items-center justify-center overflow-hidden border-2 border-[#3B82F6]/40">
                    {viewTeacherProfile.profile_photo ? (
                      <img src={viewTeacherProfile.profile_photo.startsWith('/') ? `${API_BASE}${viewTeacherProfile.profile_photo}` : viewTeacherProfile.profile_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Briefcase className="w-7 h-7 text-blue-700 font-bold" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{viewTeacherProfile.name}</h3>
                    <p className="text-sm text-slate-500 font-semibold">{viewTeacherProfile.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white/20 rounded-xl p-3 text-center border border-slate-200">
                    <p className="text-xl font-bold text-blue-700 font-bold">{viewTeacherProfile.students_count || 0}</p>
                    <p className="text-xs text-slate-500 font-semibold">Students</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center border border-slate-200">
                    <p className="text-xl font-bold text-emerald-700 font-bold">{viewTeacherProfile.courses_count || 0}</p>
                    <p className="text-xs text-slate-500 font-semibold">Courses</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center border border-slate-200">
                    <p className="text-xl font-bold text-amber-400">{viewTeacherProfile.rating || '4.8'}</p>
                    <p className="text-xs text-slate-500 font-semibold">Rating</p>
                  </div>
                </div>
                {viewTeacherProfile.bio && (
                  <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-200">
                    <p className="text-xs text-slate-500 font-semibold mb-1 font-bold">Bio</p>
                    <p className="text-sm text-slate-200">{viewTeacherProfile.bio}</p>
                  </div>
                )}
                <div className="pt-2 border-t border-slate-200 space-y-2">
                  <p className="text-xs text-slate-500 font-semibold font-bold mb-2">Share Profile</p>
                  <div className="flex gap-2">
                    <input readOnly value={`${window.location.origin}/teacher-profile/${viewTeacherProfile.id}`}
                      className="flex-1 bg-slate-800 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-300" data-testid="share-link-input" />
                    <Button size="sm" className="bg-[#3B82F6] hover:bg-blue-600 text-white"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/teacher-profile/${viewTeacherProfile.id}`); toast.success('Link copied!'); }}
                      data-testid="copy-link-btn">
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Teacher Dialog */}
        <Dialog open={!!editTeacher} onOpenChange={(open) => { if (!open) setEditTeacher(null); }}>
          <DialogContent className="bg-white border border-slate-200 shadow-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A] text-lg font-extrabold">Edit Teacher Details</DialogTitle>
              <DialogDescription className="text-[#64748B] font-semibold">Update teacher information</DialogDescription>
            </DialogHeader>
            {editTeacher && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.put(`/admin/teachers/${editTeacher.id}`, {
                    name: editTeacher.name, email: editTeacher.email,
                    phone: editTeacher.phone || '', username: editTeacher.username || ''
                  });
                  toast.success('Teacher updated successfully');
                  setEditTeacher(null);
                  const res = await api.get('/admin/users');
                  setTeachers(res.data.filter(u => u.role === 'teacher'));
                } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
              }} className="space-y-4 mt-3">
                <div><Label className="font-extrabold text-xs text-[#334155] uppercase">Name</Label><Input value={editTeacher.name || ''} onChange={e => setEditTeacher({...editTeacher, name: e.target.value})} required className="h-10 rounded-xl bg-slate-50 border-slate-200 text-[#0F172A] font-bold" data-testid="edit-teacher-name" /></div>
                <div><Label className="font-extrabold text-xs text-[#334155] uppercase">Email</Label><Input type="email" value={editTeacher.email || ''} onChange={e => setEditTeacher({...editTeacher, email: e.target.value})} required className="h-10 rounded-xl bg-slate-50 border-slate-200 text-[#0F172A] font-bold" data-testid="edit-teacher-email" /></div>
                <div><Label className="font-extrabold text-xs text-[#334155] uppercase">Phone Number</Label><Input value={editTeacher.phone || ''} onChange={e => setEditTeacher({...editTeacher, phone: e.target.value})} placeholder="+91 98765 43210" className="h-10 rounded-xl bg-slate-50 border-slate-200 text-[#0F172A] font-bold" data-testid="edit-teacher-phone" /></div>
                <div><Label className="font-extrabold text-xs text-[#334155] uppercase">Username</Label><Input value={editTeacher.username || ''} onChange={e => setEditTeacher({...editTeacher, username: e.target.value})} className="h-10 rounded-xl bg-slate-50 border-slate-200 text-[#0F172A] font-bold" data-testid="edit-teacher-username" /></div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditTeacher(null)} className="flex-1 h-10 rounded-xl font-bold">Cancel</Button>
                  <Button type="submit" className="flex-1 h-10 rounded-xl bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-extrabold" data-testid="save-teacher-btn">Save Changes</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Students Section */}
        {activeSection === 'students' && (
          <StudentsSection
            students={students} setStudents={setStudents}
            teachers={teachers} allCourses={allCourses}
            managingStudent={managingStudent}
            classStats={classStats} classLogData={classLogData}
            createStudentOpen={createStudentOpen} setCreateStudentOpen={setCreateStudentOpen}
            editingProfile={editingProfile} setEditingProfile={setEditingProfile}
            manageStudentOpen={manageStudentOpen} setManageStudentOpen={setManageStudentOpen}
            classLogOpen={classLogOpen} setClassLogOpen={setClassLogOpen}
            showRenewal={showRenewal} setShowRenewal={setShowRenewal}
            newStudent={newStudent} setNewStudent={setNewStudent}
            isCreating={isCreating} handleCreateStudent={handleCreateStudent}
            toggleStudentVertical={toggleStudentVertical}
            profileForm={profileForm} setProfileForm={setProfileForm}
            savingProfile={savingProfile} handleSaveProfile={handleSaveProfile}
            toggleProfileVertical={toggleProfileVertical}
            studentProfileEdit={studentProfileEdit} setStudentProfileEdit={setStudentProfileEdit}
            savingStudentProfile={savingStudentProfile} handleSaveStudentProfile={handleSaveStudentProfile}
            studentMappings={studentMappings} setStudentMappings={setStudentMappings}
            savingMapping={savingMapping} handleSaveTeacher={handleSaveTeacher}
            toggleCourse={toggleCourse} handleSaveCourses={handleSaveCourses}
            toggleModule={toggleModule} handleSaveModules={handleSaveModules}
            classAllocation={classAllocation} setClassAllocation={setClassAllocation}
            handleSaveClasses={handleSaveClasses}
            renewalData={renewalData} setRenewalData={setRenewalData}
            renewalTotalINR={renewalTotalINR} handleRenewal={handleRenewal}
            openStudentManagement={openStudentManagement} fetchClassLog={fetchClassLog}
            VERTICALS={VERTICALS} COUNTRIES={COUNTRIES} STATE_MAP={STATE_MAP}
            AVAILABLE_MODULES={AVAILABLE_MODULES} USD_TO_INR={USD_TO_INR}
            MODULES_BY_VERTICAL={modulesByVertical} modulesForVertical={modulesForVertical}
            moduleCatalogItems={moduleCatalogItems} refetchModuleCatalog={fetchVerticalModules}
            mappingVertical={mappingVertical} setMappingVertical={setMappingVertical}
            onOpenProfileCard={(sid) => setProfileCardStudentId(sid)}
          />
        )}
        {activeSection === 'passwords' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900">User Credentials</h1>
              <p className="text-slate-500 font-semibold">All usernames and passwords for platform users</p>
            </div>

            <div className="glass-card rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-500/10">
                    <TableHead className="font-semibold text-blue-700 font-bold">Name</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Role</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Email / Username</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Password</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {credentials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 font-semibold py-8">No credentials found</TableCell>
                    </TableRow>
                  ) : (
                    credentials.map((cred) => (
                      <TableRow key={cred.id} className="hover:bg-slate-500/10" data-testid={`cred-row-${cred.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              cred.role === 'admin' ? 'bg-red-100' : cred.role === 'teacher' ? 'bg-blue-100' : cred.role === 'student' ? 'bg-emerald-100' : 'bg-amber-100'
                            }`}>
                              <KeyRound className={`w-5 h-5 ${
                                cred.role === 'admin' ? 'text-red-600' : cred.role === 'teacher' ? 'text-blue-600' : cred.role === 'student' ? 'text-emerald-600' : 'text-amber-600'
                              }`} />
                            </div>
                            <span className="font-bold text-slate-900">{cred.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            cred.role === 'admin' ? 'bg-red-100 text-red-700' : cred.role === 'teacher' ? 'bg-blue-100 text-blue-600 font-bold' : cred.role === 'student' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {cred.role?.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-slate-700 font-semibold">{cred.username || cred.email}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-slate-700 font-semibold">
                            {showPasswords[cred.id] ? cred.plain_password : '••••••••'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setShowPasswords(prev => ({ ...prev, [cred.id]: !prev[cred.id] }))}
                              data-testid={`toggle-pwd-${cred.id}`}
                            >
                              {showPasswords[cred.id] ? <EyeOff className="w-4 h-4 text-slate-500 font-semibold" /> : <Eye className="w-4 h-4 text-slate-500 font-semibold" />}
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(cred.plain_password || '');
                                toast.success(`Password copied for ${cred.name}`);
                              }}
                              data-testid={`copy-pwd-${cred.id}`}
                            >
                              <Copy className="w-4 h-4 text-slate-500 font-semibold" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={async () => {
                                const pwd = prompt(`Reset password for ${cred.name}:`, cred.plain_password || `${cred.name.split(' ')[0]}@123`);
                                if (pwd && pwd.length >= 4) {
                                  try {
                                    await api.post(`/admin/reset-password/${cred.id}`, { password: pwd });
                                    toast.success(`Password reset for ${cred.name}`);
                                    const res = await api.get('/admin/users');
                                    setCredentials(res.data.filter(u => u.plain_password));
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                                }
                              }}
                              data-testid={`reset-pwd-${cred.id}`}
                              title="Reset password"
                            >
                              <RefreshCw className="w-4 h-4 text-amber-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Files & Media */}
        {activeSection === 'my-files' && <MyFilesPage />}

        {/* Receipt Approvals */}
        {activeSection === 'receipts' && <ReceiptApprovalsPanel />}

        {/* Weekly Feedback Digest */}
        {activeSection === 'digest' && <DigestAdminPanel />}


        {/* Admin Tools Section */}
        {activeSection === 'tools' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900" data-testid="tools-title">Admin Tools</h1>
              <p className="text-slate-500 font-semibold">System management and maintenance tools</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
              <div className="glass-card rounded-xl p-6 border-2 border-red-200" data-testid="global-reset-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Global Reset</h3>
                    <p className="text-xs text-red-500 font-bold">Destructive action — cannot be undone</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Deletes ALL assigned curriculum, resets class progress and scheduled classes to 0. Preserves: user accounts, passwords, master templates, quizzes, and payment history.</p>
                <Button onClick={async () => {
                  if (!window.confirm('WARNING: This will delete ALL assigned curriculum and reset ALL class progress.\n\nUser accounts, passwords, templates, and quizzes will be preserved.\n\nType RESET_ALL to confirm.')) return;
                  const input = window.prompt('Type RESET_ALL to confirm:');
                  if (input !== 'RESET_ALL') { toast.error('Reset cancelled'); return; }
                  try {
                    const res = await api.post('/admin/global-reset', { confirm: 'RESET_ALL' });
                    toast.success('Global reset completed: ' + JSON.stringify(res.data.results));
                  } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
                }} className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg" data-testid="global-reset-btn">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Execute Global Reset
                </Button>
              </div>
              <div className="glass-card rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><RefreshCw className="w-6 h-6 text-blue-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Data Cleanup</h3>
                    <p className="text-xs text-blue-500 font-bold">Safe operation — fixes inconsistencies</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Marks past scheduled classes as missed, recalculates class credits, and backfills INR conversion rates on old payments.</p>
                <Button onClick={async () => {
                  if (!window.confirm('Run data cleanup? This will fix data inconsistencies.')) return;
                  try {
                    const res = await api.post('/admin/data-cleanup', { delete_test_payments: false, recalculate_credits: true, mark_past_classes_missed: true, backfill_inr: true });
                    toast.success('Cleanup completed: ' + JSON.stringify(res.data.results));
                  } catch (e) { toast.error(e.response?.data?.detail || 'Cleanup failed'); }
                }} className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg">
                  <RefreshCw className="w-4 h-4 mr-2" /> Run Data Cleanup
                </Button>
              </div>
              <div className="glass-card rounded-xl p-6 border-2 border-emerald-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><RefreshCw className="w-6 h-6 text-emerald-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Restore Student Data</h3>
                    <p className="text-xs text-emerald-600 font-bold">Safe — creates missing students & records</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Restores all 6 students (CITT001-CITT006), enrollments, invoices, class credits, and completed class records. Skips if data already exists.</p>
                <Button onClick={async () => {
                  if (!window.confirm('Restore all student data? This will create students, enrollments, invoices, and class records.')) return;
                  try {
                    const res = await api.post('/admin/restore-student-data');
                    if (res.data.status === 'success') {
                      toast.success(res.data.message);
                      window.location.reload();
                    } else {
                      toast.error('Error: ' + res.data.message);
                    }
                  } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
                }} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg" data-testid="restore-data-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Restore Student Data
                </Button>
              </div>

              {/* Merge Duplicate Teachers */}
              <div className="glass-card rounded-xl p-6 border-2 border-purple-200" data-testid="merge-teachers-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center"><Users className="w-6 h-6 text-purple-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Merge Duplicate Teachers</h3>
                    <p className="text-xs text-purple-500 font-bold">Keeps @gmail.com variant, rewrites all references</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Detects teacher rows sharing a first-name (e.g., Pragya × 2, Ankita × 2). Keeps the one with an <code className="text-xs bg-purple-50 px-1 rounded">@gmail.com</code> email, migrates <b>every</b> reference across 23 collections (scheduled classes, curriculum, quizzes, chats, payouts, mapped students…) then deletes the duplicate. Try a dry-run first.</p>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        const res = await api.post('/admin/merge-duplicate-teachers', { dry_run: true, preferred_emails: ['@gmail.com'] });
                        const groups = res.data?.duplicate_groups_found ?? 0;
                        const summary = (res.data?.merges || []).map(m => `${m.group}: keep ${m.primary.email}, drop ${(m.merged_into_primary || []).map(d => d.email).join(', ')}`).join('\n');
                        window.alert(`Dry-run — ${groups} duplicate group(s) found:\n\n${summary || 'No duplicates.'}`);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Dry-run failed'); }
                    }}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold rounded-lg"
                    data-testid="merge-teachers-dryrun-btn"
                  >
                    Dry Run
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Execute the merge? This is a one-way operation.\n\nRun a dry-run first if you haven’t.')) return;
                      try {
                        const res = await api.post('/admin/merge-duplicate-teachers', { preferred_emails: ['@gmail.com'] });
                        toast.success(`Merged ${res.data?.duplicate_groups_found ?? 0} duplicate group(s)`);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg"
                    data-testid="merge-teachers-btn"
                  >
                    <Users className="w-4 h-4 mr-2" /> Execute Merge
                  </Button>
                </div>
              </div>

              {/* Math Olympiad Builder */}
              <div className="glass-card rounded-xl p-6 border-2 border-orange-200" data-testid="olympiad-builder-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center"><Award className="w-6 h-6 text-orange-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Math Olympiad</h3>
                    <p className="text-xs text-orange-500 font-bold">Create weekly challenge — auto-deactivates prior</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Publish a new themed weekly Math Olympiad challenge. Students see it in their Achievements tab; badges + leaderboard update automatically.</p>
                <Button
                  onClick={() => setOlympiadBuilderOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg"
                  data-testid="olympiad-builder-open"
                >
                  <Award className="w-4 h-4 mr-2" /> Create Challenge
                </Button>
              </div>

              {/* Curriculum Progression */}
              <div className="glass-card rounded-xl p-6 border-2 border-indigo-200" data-testid="curriculum-progression-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center"><Users className="w-6 h-6 text-indigo-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Curriculum Progression</h3>
                    <p className="text-xs text-indigo-500 font-bold">Locked/Flexible toggle · Reset progress</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Manage per-student curriculum progression. In <b>Locked</b> mode teachers can only pick the recommended next topic; <b>Flexible</b> lets them pick any pending topic. Reset a curriculum if a student needs a fresh start.</p>
                <Button
                  onClick={() => setCurriculumMgrOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg"
                  data-testid="curriculum-mgr-open"
                >
                  <BookOpen className="w-4 h-4 mr-2" /> Open Progression Manager
                </Button>
              </div>


              {/* Apply Teacher Mappings */}
              <div className="glass-card rounded-xl p-6 border-2 border-amber-200" data-testid="apply-mappings-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-amber-600" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">Apply Teacher Mappings</h3>
                    <p className="text-xs text-amber-600 font-bold">Reconciles the master student-to-teacher list</p>
                  </div>
                </div>
                <p className="text-sm text-[#334155] mb-4 leading-relaxed">Applies the canonical CodeITT mapping:<br />
                  <span className="text-xs font-bold text-[#334155]">Pragya → Prithavi, Ruhani, Anurag · Ankita → Baiyzaa, Lakshita, Abhimanyu · Usama → Ariel, Reyansh · Mehak → Kimayra, Reyansh Mall · Mangala → Shresht</span>
                </p>
                <Button
                  onClick={async () => {
                    if (!window.confirm('Apply the canonical Pragya/Ankita/Usama/Mehak/Mangala student mappings? Safe to re-run.')) return;
                    try {
                      const res = await api.post('/admin/apply-teacher-mappings', {
                        mappings: [
                          { teacher_name: 'Pragya',  students: ['Prithavi', 'Ruhani', 'Anurag'] },
                          { teacher_name: 'Ankita',  students: ['Baiyzaa', 'Lakshita', 'Abhimanyu'] },
                          { teacher_name: 'Usama',   students: ['Ariel', 'Reyansh'] },
                          { teacher_name: 'Mehak',   students: ['Kimayra', 'Reyansh Mall'] },
                          { teacher_name: 'Mangala', students: ['Shresht'] },
                        ],
                      });
                      const applied = res.data?.applied?.length ?? 0;
                      const unresolved = (res.data?.unresolved_students || []).map(u => `${u.student_token} (→ ${u.teacher})`).join(', ');
                      toast.success(`Applied ${applied} mapping(s)${unresolved ? ` · Unresolved: ${unresolved}` : ''}`);
                    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg"
                  data-testid="apply-mappings-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Apply Canonical Mappings
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Revenue Section */}
        {activeSection === 'revenue' && revenueStats && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Revenue</h1>
                <p className="text-slate-500 font-semibold">Financial overview — includes all payments, renewals & purchase confirmations</p>
              </div>
              <Button onClick={() => setAddRevenueOpen(true)} className="bg-gray-50 border border-green-400/40 text-green-700 font-bold hover:bg-green-50" data-testid="add-revenue-btn"><Plus className="w-4 h-4 mr-1" /> Add Revenue</Button>
            </div>

            {/* Add Revenue Dialog */}
            <Dialog open={addRevenueOpen} onOpenChange={setAddRevenueOpen}>
              <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Manual Revenue</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div><Label>Student Name</Label><Input value={revenueForm.student_name} onChange={e => setRevenueForm(p => ({...p, student_name: e.target.value}))} placeholder="Student name" data-testid="revenue-student" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Date</Label><Input type="date" value={revenueForm.date_of_transaction} onChange={e => setRevenueForm(p => ({...p, date_of_transaction: e.target.value}))} /></div>
                    <div><Label>Vertical</Label><Input value={revenueForm.vertical} onChange={e => setRevenueForm(p => ({...p, vertical: e.target.value}))} placeholder="e.g. Coding" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div><Label>Classes Added</Label><Input type="number" value={revenueForm.classes_added} onChange={e => setRevenueForm(p => ({...p, classes_added: Number(e.target.value)}))} /></div>
                    <div><Label>Amount</Label><Input type="number" value={revenueForm.amount} onChange={e => setRevenueForm(p => ({...p, amount: Number(e.target.value)}))} data-testid="revenue-amount" /></div>
                    <div><Label>Currency</Label><Select value={revenueForm.currency} onValueChange={v => setRevenueForm(p => ({...p, currency: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="INR">INR</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="CAD">CAD</SelectItem></SelectContent></Select></div>
                  </div>
                  <div><Label>Payment Method</Label><Input value={revenueForm.payment_method} onChange={e => setRevenueForm(p => ({...p, payment_method: e.target.value}))} placeholder="e.g. Bank Transfer" /></div>
                  <div><Label>Notes</Label><Input value={revenueForm.notes} onChange={e => setRevenueForm(p => ({...p, notes: e.target.value}))} /></div>
                  <Button onClick={handleAddRevenue} className="w-full bg-green-600 hover:bg-green-700">Add Revenue</Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-blue-400/30">
                <div className="text-sm text-slate-500 font-semibold mb-1">Today</div>
                <div className="text-3xl font-bold text-blue-600">₹{(revenueStats.today?.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-500 font-semibold">{revenueStats.today?.count || 0} payments</div>
              </div>

              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-emerald-400/30">
                <div className="text-sm text-slate-500 font-semibold mb-1">This Week</div>
                <div className="text-3xl font-bold text-emerald-600">₹{(revenueStats.week?.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-500 font-semibold">{revenueStats.week?.count || 0} payments</div>
              </div>

              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-violet-400/30">
                <div className="text-sm text-slate-500 font-semibold mb-1">This Month</div>
                <div className="text-3xl font-bold text-violet-600">₹{(revenueStats.month?.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-500 font-semibold">{revenueStats.month?.count || 0} payments</div>
              </div>

              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-amber-400/30">
                <div className="text-sm text-slate-500 font-semibold mb-1">This Quarter</div>
                <div className="text-3xl font-bold text-amber-600">₹{(revenueStats.quarter?.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-500 font-semibold">{revenueStats.quarter?.count || 0} payments</div>
              </div>

              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-rose-200">
                <div className="text-sm text-slate-500 font-semibold mb-1">This Year</div>
                <div className="text-3xl font-bold text-rose-600">₹{(revenueStats.year?.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-500 font-semibold">{revenueStats.year?.count || 0} payments</div>
              </div>
            </div>

            {/* Transactions / Invoices Toggle */}
            <div className="flex gap-2 mb-6">
              <button onClick={() => setShowInvoiceTab(false)} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${!showInvoiceTab ? 'bg-[#1E3A8A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="tab-transactions">Transactions</button>
              <button onClick={() => { setShowInvoiceTab(true); fetchInvoices(); }} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${showInvoiceTab ? 'bg-[#1E3A8A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="tab-invoices">Invoices ({invoices.length})</button>
            </div>

            {showInvoiceTab ? (
              <div className="nb-card overflow-hidden" data-testid="invoices-section">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-[#0F172A]">Generated Invoices</h3>
                </div>
                {invoices.length === 0 ? (
                  <div className="p-10 text-center text-slate-400 font-bold">No invoices generated yet. Mark a payment as paid or generate manually from transactions.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 text-left text-xs font-bold text-slate-500 uppercase">
                        <th className="p-3">Invoice #</th><th className="p-3">Date</th><th className="p-3">Student Name</th><th className="p-3">Type</th><th className="p-3">Classes</th><th className="p-3">Amount</th><th className="p-3">Method</th><th className="p-3">Actions</th>
                      </tr></thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                            <td className="p-3 font-bold text-[#1E3A8A]">{inv.invoice_number}</td>
                            <td className="p-3 text-slate-600">{inv.date}</td>
                            <td className="p-3 font-bold text-[#0F172A]">{inv.student_name}</td>
                            <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${inv.type === 'Enrollment' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{inv.type}</span></td>
                            <td className="p-3 font-bold">{inv.num_classes}</td>
                            <td className="p-3 font-bold text-[#0F172A]">{inv.currency_symbol}{(inv.amount || 0).toLocaleString()}</td>
                            <td className="p-3 text-slate-600 capitalize">{(inv.payment_method || '').replace(/_/g,' ')}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleDownloadInvoicePdf(inv.id)} className="px-3 py-1.5 bg-[#1E3A8A] text-white rounded-lg text-xs font-bold hover:bg-[#1D4ED8] transition-colors" data-testid={`download-invoice-${inv.invoice_number}`}>Download PDF</button>
                                <button onClick={() => { const url = `${API}/api/invoices/${inv.id}/pdf`; navigator.clipboard.writeText(url); toast.success('Link copied!'); }} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200 transition-colors">Copy Link</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
            <>
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white mb-8">
              <h2 className="text-2xl font-bold mb-2">Total Revenue (INR)</h2>
              <div className="text-5xl font-bold">₹{(revenueStats.total?.amount || 0).toLocaleString()}</div>
              <p className="text-blue-100 mt-2">{revenueStats.total?.count || 0} total payments — all converted to INR</p>
            </div>

            {/* Currency Breakdown */}
            {revenueStats.currency_breakdown && Object.keys(revenueStats.currency_breakdown).length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {Object.entries(revenueStats.currency_breakdown).map(([cur, data]) => (
                  <div key={cur} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid={`currency-${cur}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-black text-slate-800">{cur}</span>
                      <span className="text-xs text-slate-400">{data.count} payment{data.count !== 1 ? 's' : ''}</span>
                    </div>
                    <p className="text-lg font-bold text-slate-900">{cur === 'INR' ? '₹' : ''}{data.original_total?.toLocaleString()} {cur}</p>
                    {cur !== 'INR' && <p className="text-xs text-blue-600 font-bold mt-0.5">= ₹{data.inr_total?.toLocaleString()} INR</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Recent Transactions */}
            {revenueStats.recent && revenueStats.recent.length > 0 && (
              <div className="glass-card rounded-xl overflow-x-auto">
                <div className="px-6 py-4 border-b bg-blue-500/10"><h2 className="font-bold text-blue-700 font-bold">Recent Payments</h2></div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Student</TableHead>
                      <TableHead className="font-semibold">Source</TableHead>
                      <TableHead className="font-semibold">Method</TableHead>
                      <TableHead className="font-semibold">Original Amt</TableHead>
                      <TableHead className="font-semibold">Currency</TableHead>
                      <TableHead className="font-semibold">Rate</TableHead>
                      <TableHead className="font-semibold text-right">INR Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {revenueStats.recent.map((item, idx) => (
                      <TableRow key={`${item.created_at}-${item.student_name}-${idx}`}>
                        <TableCell className="text-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="font-medium">{item.student_name || '—'}</TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.source === 'renewal' ? 'bg-emerald-100 text-emerald-700' : item.source === 'fresh' ? 'bg-blue-100 text-blue-600 font-bold' : item.source === 'enrollment' ? 'bg-blue-100 text-blue-600 font-bold' : item.source === 'purchase' ? 'bg-violet-100 text-violet-600 font-bold' : 'bg-slate-100 text-slate-700 font-semibold'}`}>{item.source === 'fresh' ? 'Fresh Enrollment' : item.source === 'enrollment' ? 'Enrollment' : item.source === 'renewal' ? 'Renewal' : item.source === 'purchase' ? 'Purchase' : item.source || '—'}</span></TableCell>
                        <TableCell><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{PAYMENT_METHOD_LABELS[item.payment_method] || item.payment_method || '—'}</span></TableCell>
                        <TableCell className="text-sm">{(item.original_amount || item.amount || 0).toLocaleString()}</TableCell>
                        <TableCell><span className="px-1.5 py-0.5 rounded bg-slate-100 text-xs font-bold">{item.currency || 'INR'}</span></TableCell>
                        <TableCell className="text-xs text-slate-500">{item.exchange_rate || (item.currency === 'INR' || !item.currency ? '1' : '—')}</TableCell>
                        <TableCell className="text-right font-bold">₹{(item.amount_inr || item.amount || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Manual Revenues */}
            {manualRevenues.length > 0 && (
              <div className="glass-card rounded-xl overflow-x-auto mt-6">
                <div className="px-6 py-4 border-b bg-green-500/10"><h2 className="font-bold text-green-900">Manual Revenue Entries</h2></div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="font-semibold">Date</TableHead><TableHead className="font-semibold">Student</TableHead><TableHead className="font-semibold">Vertical</TableHead><TableHead className="font-semibold">Classes</TableHead><TableHead className="font-semibold">Amount</TableHead><TableHead className="font-semibold text-right">INR Value</TableHead><TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {manualRevenues.map(r => (
                      <TableRow key={r.id}>
                        {editingRevenue === r.id ? (
                          <>
                            <TableCell><Input type="date" value={editRevenueData.date_of_transaction || ''} onChange={e => setEditRevenueData(p => ({...p, date_of_transaction: e.target.value}))} className="h-7 text-xs w-28" /></TableCell>
                            <TableCell><Input value={editRevenueData.student_name || ''} onChange={e => setEditRevenueData(p => ({...p, student_name: e.target.value}))} className="h-7 text-xs" /></TableCell>
                            <TableCell><Input value={editRevenueData.vertical || ''} onChange={e => setEditRevenueData(p => ({...p, vertical: e.target.value}))} className="h-7 text-xs w-20" /></TableCell>
                            <TableCell><Input type="number" value={editRevenueData.classes_added || 0} onChange={e => setEditRevenueData(p => ({...p, classes_added: parseInt(e.target.value) || 0}))} className="h-7 text-xs w-16" /></TableCell>
                            <TableCell><Input type="number" value={editRevenueData.amount || 0} onChange={e => setEditRevenueData(p => ({...p, amount: parseFloat(e.target.value) || 0}))} className="h-7 text-xs w-20" /></TableCell>
                            <TableCell><Input type="number" value={editRevenueData.amount_inr || 0} onChange={e => setEditRevenueData(p => ({...p, amount_inr: parseFloat(e.target.value) || 0}))} className="h-7 text-xs w-20" /></TableCell>
                            <TableCell className="text-center">
                              <div className="flex gap-1 justify-center">
                                <Button variant="ghost" size="sm" onClick={handleSaveRevenueEdit} className="text-emerald-600 h-7 w-7 p-0" data-testid={`save-revenue-${r.id}`}><Save className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" onClick={() => setEditingRevenue(null)} className="text-slate-500 font-semibold h-7 w-7 p-0"><X className="w-3.5 h-3.5" /></Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-sm">{r.date_of_transaction}</TableCell>
                            <TableCell className="font-medium">{r.student_name}</TableCell>
                            <TableCell className="text-sm">{r.vertical}</TableCell>
                            <TableCell className="text-center">{r.classes_added}</TableCell>
                            <TableCell className="font-semibold">{r.currency === 'INR' ? '₹' : r.currency === 'USD' ? '$' : 'C$'}{r.amount?.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-bold text-green-600">₹{r.amount_inr?.toLocaleString()}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex gap-1 justify-center">
                                <Button variant="ghost" size="sm" onClick={() => { setEditingRevenue(r.id); setEditRevenueData({...r}); }} className="text-blue-600 h-7 w-7 p-0" data-testid={`edit-revenue-${r.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteRevenue(r.id)} className="text-red-500 h-7 w-7 p-0" data-testid={`delete-revenue-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* Profit Section */}
        {activeSection === 'profit' && profitStats && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900" data-testid="profit-title">Profit Analysis</h1>
                <p className="text-slate-500 font-semibold">Net Profit = Total Revenue (INR) - Total Teacher Payouts (INR)</p>
              </div>
            </div>

            {/* Profit KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-emerald-400/30" data-testid="kpi-total-revenue">
                <div className="text-sm text-slate-500 font-semibold mb-1">Total Revenue</div>
                <div className="text-3xl font-bold text-emerald-600">₹{(profitStats.total_revenue || 0).toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">All sources combined (INR)</div>
              </div>
              <div className="glass-card rounded-xl p-6 shadow-sm border-2 border-red-400/30" data-testid="kpi-total-payouts">
                <div className="text-sm text-slate-500 font-semibold mb-1">Total Payouts</div>
                <div className="text-3xl font-bold text-red-600">₹{(profitStats.total_payouts || 0).toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">Teacher payouts (INR)</div>
              </div>
              <div className={`glass-card rounded-xl p-6 shadow-sm border-2 ${(profitStats.net_profit || 0) >= 0 ? 'border-blue-400/30' : 'border-red-400/30'}`} data-testid="kpi-net-profit">
                <div className="text-sm text-slate-500 font-semibold mb-1">Net Profit</div>
                <div className={`text-3xl font-bold ${(profitStats.net_profit || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {(profitStats.net_profit || 0) < 0 ? '-' : ''}₹{Math.abs(profitStats.net_profit || 0).toLocaleString()}
                </div>
                <div className="text-xs text-slate-400 mt-1">Revenue minus payouts</div>
              </div>
              <div className={`glass-card rounded-xl p-6 shadow-sm border-2 ${(profitStats.profit_margin || 0) >= 0 ? 'border-violet-400/30' : 'border-red-400/30'}`} data-testid="kpi-profit-margin">
                <div className="text-sm text-slate-500 font-semibold mb-1">Profit Margin</div>
                <div className={`text-3xl font-bold ${(profitStats.profit_margin || 0) >= 0 ? 'text-violet-600' : 'text-red-600'}`}>{profitStats.profit_margin || 0}%</div>
                <div className="text-xs text-slate-400 mt-1">(Net Profit / Revenue) x 100</div>
              </div>
            </div>

            {/* Refund & Breakdown */}
            {profitStats.total_refunded > 0 && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200 mb-8 flex items-center gap-3" data-testid="refund-notice">
                <RotateCcw className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-700">₹{profitStats.total_refunded.toLocaleString()} in refunds have been deducted from revenue</p>
                  <p className="text-xs text-red-500">Refund amounts are subtracted from the total revenue figure above</p>
                </div>
              </div>
            )}

            {/* Revenue Breakdown */}
            {profitStats.breakdown && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                  <p className="text-xs text-slate-500 font-semibold">Enrollments</p>
                  <p className="text-xl font-bold text-slate-900">₹{(profitStats.breakdown.enrollment_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                  <p className="text-xs text-slate-500 font-semibold">Manual Revenue</p>
                  <p className="text-xl font-bold text-slate-900">₹{(profitStats.breakdown.manual_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                  <p className="text-xs text-slate-500 font-semibold">Renewals</p>
                  <p className="text-xl font-bold text-slate-900">₹{(profitStats.breakdown.renewal_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                  <p className="text-xs text-slate-500 font-semibold">Purchases</p>
                  <p className="text-xl font-bold text-slate-900">₹{(profitStats.breakdown.purchase_revenue || 0).toLocaleString()}</p>
                </div>
              </div>
            )}

            {/* Monthly Profit Chart */}
            {profitStats.monthly_chart && profitStats.monthly_chart.length > 0 && (
              <div className="nb-card p-6 mb-8" data-testid="monthly-profit-chart">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  <h3 className="font-bold text-slate-900 text-base">Monthly Revenue vs Payouts vs Profit</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={profitStats.monthly_chart} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#0F172A' }} formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="payouts" name="Payouts" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" name="Profit" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Per-Teacher Profit Analysis */}
            {profitStats.per_teacher && profitStats.per_teacher.length > 0 && (
              <div className="glass-card rounded-xl overflow-x-auto" data-testid="per-teacher-profit">
                <div className="px-6 py-4 border-b bg-violet-500/10">
                  <h2 className="font-bold text-violet-700">Per-Teacher Profit Analysis</h2>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Teacher</TableHead>
                      <TableHead className="font-semibold text-center">Students</TableHead>
                      <TableHead className="font-semibold text-right">Revenue (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Payout (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Profit (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Margin %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitStats.per_teacher.map((t) => (
                      <TableRow key={t.teacher_id}>
                        <TableCell className="font-bold text-sm">{t.teacher_name}</TableCell>
                        <TableCell className="text-center text-sm">{t.students}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-600 font-bold">₹{t.revenue_inr.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm text-red-600 font-bold">₹{t.payout_inr.toLocaleString()}</TableCell>
                        <TableCell className={`text-right text-sm font-bold ${t.profit_inr >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {t.profit_inr < 0 ? '-' : ''}₹{Math.abs(t.profit_inr).toLocaleString()}
                        </TableCell>
                        <TableCell className={`text-right text-sm font-bold ${t.margin_pct >= 0 ? 'text-violet-600' : 'text-red-600'}`}>{t.margin_pct}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {activeSection === 'demos' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Demo Management</h1>
                <p className="text-slate-500 font-semibold">Manage demo bookings, teacher eligibility & assignments</p>
              </div>
              <Dialog open={createDemoOpen} onOpenChange={setCreateDemoOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Book Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-blue-200 shadow-xl">
                  <DialogHeader>
                    <DialogTitle className="text-blue-700 font-bold">Book New Demo</DialogTitle>
                    <DialogDescription>Schedule a demo for a prospective customer</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateDemo} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-700 font-semibold">Name</Label>
                      <Input
                        value={newDemo.name}
                        onChange={(e) => setNewDemo({...newDemo, name: e.target.value})}
                        required
                        className="mt-1 border-2 border-blue-400/30"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Email</Label>
                      <Input
                        type="email"
                        value={newDemo.email}
                        onChange={(e) => setNewDemo({...newDemo, email: e.target.value})}
                        required
                        className="mt-1 border-2 border-blue-400/30"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Phone</Label>
                      <Input
                        value={newDemo.phone}
                        onChange={(e) => setNewDemo({...newDemo, phone: e.target.value})}
                        className="mt-1 border-2 border-blue-400/30"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Scheduled Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={newDemo.scheduled_date}
                        onChange={(e) => setNewDemo({...newDemo, scheduled_date: e.target.value})}
                        required
                        className="mt-1 border-2 border-blue-400/30"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Timezone</Label>
                      <Select value={newDemo.timezone} onValueChange={(val) => setNewDemo({...newDemo, timezone: val})}>
                        <SelectTrigger className="mt-1 border-2 border-blue-400/30">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="America/New_York">EST (New York)</SelectItem>
                          <SelectItem value="America/Los_Angeles">PST (Los Angeles)</SelectItem>
                          <SelectItem value="Europe/London">GMT (London)</SelectItem>
                          <SelectItem value="Asia/Kolkata">IST (India)</SelectItem>
                          <SelectItem value="Asia/Singapore">SGT (Singapore)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Notes</Label>
                      <Textarea
                        value={newDemo.notes}
                        onChange={(e) => setNewDemo({...newDemo, notes: e.target.value})}
                        className="mt-1 border-2 border-blue-400/30"
                        rows={2}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={isCreating}>
                      {isCreating ? 'Creating...' : 'Book Demo'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Teacher Demo Eligibility */}
            <div className="glass-card rounded-xl p-5 mb-6 shadow-sm">
              <h2 className="font-bold text-black mb-4 flex items-center gap-2"><Settings className="w-4 h-4 text-blue-600" /> Teacher Demo Eligibility</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(demoSettings.teachers || []).filter(t => t.role === 'teacher').map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-slate-500/10 rounded-lg border border-slate-200" data-testid={`demo-toggle-${t.id}`}>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500 font-semibold">{t.email}</p>
                    </div>
                    <button onClick={() => handleToggleDemo(t.id, t.demo_eligible)} className="flex items-center gap-2">
                      {t.demo_eligible ? (
                        <span className="flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"><ToggleRight className="w-4 h-4" /> ON</span>
                      ) : (
                        <span className="flex items-center gap-1 px-3 py-1.5 bg-slate-200 text-slate-500 font-semibold rounded-full text-xs font-medium"><ToggleLeft className="w-4 h-4" /> OFF</span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Assign Demo Class */}
            <div className="bg-white rounded-xl border-2 border-amber-100 p-5 mb-6 shadow-sm">
              <h2 className="font-bold text-black mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-amber-600" /> Assign Demo Class</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div><Label className="text-xs text-slate-500 font-semibold">Student Name</Label><Input value={newDemoAssign.student_name} onChange={e => setNewDemoAssign(p => ({...p, student_name: e.target.value}))} placeholder="Demo student" className="mt-1" data-testid="demo-student-name" /></div>
                <div><Label className="text-xs text-slate-500 font-semibold">Date</Label><Input type="date" value={newDemoAssign.date} onChange={e => setNewDemoAssign(p => ({...p, date: e.target.value}))} className="mt-1" data-testid="demo-date" /></div>
                <div><Label className="text-xs text-slate-500 font-semibold">Time</Label><Input type="time" value={newDemoAssign.time} onChange={e => setNewDemoAssign(p => ({...p, time: e.target.value}))} className="mt-1" data-testid="demo-time" /></div>
                <div><Label className="text-xs text-slate-500 font-semibold">Assign To</Label>
                  <Select value={newDemoAssign.auto_assign ? 'auto' : newDemoAssign.teacher_id} onValueChange={v => { if (v === 'auto') setNewDemoAssign(p => ({...p, auto_assign: true, teacher_id: ''})); else setNewDemoAssign(p => ({...p, auto_assign: false, teacher_id: v})); }}>
                    <SelectTrigger className="mt-1" data-testid="demo-teacher-select"><SelectValue placeholder="Auto (round-robin)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (Round-Robin)</SelectItem>
                      {teachers.filter(t => t.demo_eligible).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleAssignDemo} disabled={assigningDemo} className="w-full bg-gray-50 border border-amber-400/40 text-amber-700 font-bold hover:bg-amber-50" data-testid="assign-demo-btn">
                    {assigningDemo ? 'Assigning...' : 'Assign Demo'}
                  </Button>
                </div>
              </div>
              {demoClasses.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-slate-500 font-semibold mb-2 font-medium">Recent Assigned Demos</p>
                  <div className="space-y-1">
                    {demoClasses.slice(0, 5).map(d => (
                      <div key={d.id} className="flex items-center justify-between p-2 bg-amber-500/10 rounded-lg text-sm border border-amber-100" data-testid={`assigned-demo-${d.id}`}>
                        <span className="font-medium text-slate-800 font-semibold">{d.student_name}</span>
                        <span className="text-slate-500 font-semibold">{d.date} at {d.time}</span>
                        <span className="text-amber-700 font-medium">{d.teacher_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${d.auto_assigned ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500 font-semibold'}`}>{d.auto_assigned ? 'Auto' : 'Manual'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Demo Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div onClick={() => setDemoStatusFilter('all')}
                className={`glass-card rounded-xl p-4 border-2 cursor-pointer transition-all ${demoStatusFilter === 'all' ? 'border-blue-500' : 'border-black/10 hover:border-blue-300'}`}>
                <div className="text-2xl font-bold text-blue-600">{demosData.stats?.total || 0}</div>
                <div className="text-sm text-slate-500 font-semibold">Total Leads</div>
              </div>
              <div onClick={() => setDemoStatusFilter('new')}
                className={`glass-card rounded-xl p-4 border-2 cursor-pointer transition-all ${demoStatusFilter === 'new' ? 'border-amber-500' : 'border-black/10 hover:border-amber-300'}`}>
                <div className="text-2xl font-bold text-amber-600">{demosData.stats?.new || 0}</div>
                <div className="text-sm text-slate-500 font-semibold">New</div>
              </div>
              <div onClick={() => setDemoStatusFilter('contacted')}
                className={`glass-card rounded-xl p-4 border-2 cursor-pointer transition-all ${demoStatusFilter === 'contacted' ? 'border-sky-500' : 'border-black/10 hover:border-sky-300'}`}>
                <div className="text-2xl font-bold text-sky-600">{demosData.stats?.contacted || 0}</div>
                <div className="text-sm text-slate-500 font-semibold">Contacted</div>
              </div>
              <div onClick={() => setDemoStatusFilter('completed')}
                className={`glass-card rounded-xl p-4 border-2 cursor-pointer transition-all ${demoStatusFilter === 'completed' ? 'border-emerald-500' : 'border-black/10 hover:border-emerald-300'}`}>
                <div className="text-2xl font-bold text-emerald-600">{demosData.stats?.completed || 0}</div>
                <div className="text-sm text-slate-500 font-semibold">Completed</div>
              </div>
              <div onClick={() => setDemoStatusFilter('no_show')}
                className={`glass-card rounded-xl p-4 border-2 cursor-pointer transition-all ${demoStatusFilter === 'no_show' ? 'border-red-500' : 'border-black/10 hover:border-red-300'}`}>
                <div className="text-2xl font-bold text-red-600">{demosData.stats?.no_show || 0}</div>
                <div className="text-sm text-slate-500 font-semibold">No-Show</div>
              </div>
            </div>

            {/* Filter Row */}
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant={demoStatusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setDemoStatusFilter('all')}
                className={demoStatusFilter === 'all' ? 'bg-blue-600 text-white' : 'border-blue-400/30'}
              >
                All Demos
              </Button>
              <Input
                type="date"
                value={demoDateFilter}
                onChange={(e) => setDemoDateFilter(e.target.value)}
                className="w-48 border-2 border-blue-400/30"
                placeholder="Filter by date"
              />
              {demoDateFilter && (
                <Button variant="ghost" onClick={() => setDemoDateFilter('')} className="text-slate-500 font-semibold">
                  Clear
                </Button>
              )}
            </div>

            {/* Demos Table */}
            <div className="glass-card rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-500/10">
                    <TableHead className="font-semibold text-blue-700 font-bold">Student</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Contact</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Details</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Schedule</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Status</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDemos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 font-semibold py-8">
                        No demo leads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDemos.map((demo) => (
                      <TableRow key={demo.id} className="hover:bg-slate-500/10">
                        <TableCell>
                          <div className="font-medium">{demo.name}</div>
                          {demo.age && <div className="text-xs text-slate-500 font-semibold">Age: {demo.age}</div>}
                          {demo.grade && <div className="text-xs text-slate-500 font-semibold">{demo.grade}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{demo.email}</div>
                          {demo.phone && <div className="text-xs text-slate-500 font-semibold">{demo.phone}</div>}
                          {demo.country && <div className="text-xs text-slate-500 font-semibold">{demo.country}</div>}
                        </TableCell>
                        <TableCell>
                          {demo.course_interest && <div className="text-sm font-medium text-blue-600">{demo.course_interest}</div>}
                          {demo.timezone && <div className="text-xs text-slate-500 font-semibold">TZ: {demo.timezone}</div>}
                          {demo.message && <div className="text-xs text-slate-500 font-semibold mt-1 italic truncate max-w-[150px]">{demo.message}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{demo.preferred_date || demo.scheduled_date?.split('T')[0] || '-'}</div>
                          {demo.preferred_time && <div className="text-xs text-slate-500 font-semibold">{demo.preferred_time}</div>}
                          <div className="text-xs text-slate-500 font-semibold">{demo.created_at ? new Date(demo.created_at).toLocaleDateString() : ''}</div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            demo.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            demo.status === 'new' ? 'bg-amber-100 text-amber-700' :
                            demo.status === 'contacted' ? 'bg-sky-100 text-sky-700' :
                            demo.status === 'scheduled' ? 'bg-blue-100 text-blue-600 font-bold' :
                            demo.status === 'no_show' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700 font-semibold'
                          }`}>
                            {(demo.status || 'new').replace('_', ' ').toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select onValueChange={(val) => handleUpdateDemoStatus(demo.id, val)}>
                            <SelectTrigger className="w-32 h-8 text-xs border-blue-400/30">
                              <SelectValue placeholder="Update" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="scheduled">Scheduled</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="no_show">No-Show</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Courses Section */}
        {activeSection === 'courses' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900">Courses</h1>
              <p className="text-slate-500 font-semibold">{allCourses.length} courses available</p>
            </div>

            <div className="glass-card rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-500/10">
                    <TableHead className="font-semibold text-blue-700 font-bold">Course</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Teacher</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Level</TableHead>
                    <TableHead className="font-semibold text-blue-700 font-bold">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allCourses.map((course) => (
                    <TableRow key={course.id} className="hover:bg-slate-500/10">
                      <TableCell>
                        <div className="font-bold text-slate-900">{course.title}</div>
                        <div className="text-sm text-slate-500 font-semibold truncate max-w-xs">{course.description}</div>
                      </TableCell>
                      <TableCell>{course.teacher_name}</TableCell>
                      <TableCell>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          course.level === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
                          course.level === 'intermediate' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {course.level}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500 font-semibold">
                        {new Date(course.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
            )}
          </>
        )}

        {/* Curriculum Templates Section */}
        {activeSection === 'curriculum' && (
          <div className="space-y-6" data-testid="curriculum-management">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-[#0F172A]">Curriculum Management</h1>
                <p className="text-sm text-[#64748B] font-medium">Category &gt; Program &gt; Subject &gt; Module &gt; Topic &gt; Project</p>
              </div>
            </div>

            {/* ── LEVEL 1: CATEGORY GRID (3x2) ── */}
            {!currSelectedVertical && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-[#0F172A]">Categories</h2>
                  <Button onClick={() => setCurrAddVerticalOpen(true)} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl" data-testid="add-vertical-btn"><Plus className="w-4 h-4 mr-1" /> Add Category</Button>
                </div>
                <Dialog open={currAddVerticalOpen} onOpenChange={setCurrAddVerticalOpen}>
                  <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <Input placeholder="Category name (e.g. Coding)" value={currNewVertical.name} onChange={e => setCurrNewVertical(p => ({...p, name: e.target.value}))} data-testid="vertical-name-input" />
                      <Input placeholder="Description" value={currNewVertical.description} onChange={e => setCurrNewVertical(p => ({...p, description: e.target.value}))} />
                      <Button onClick={handleAddVertical} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Add Category</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-grid">
                  {currVerticals.map((v) => {
                    const iconMap = { 'Coding': Code, 'Robotics': Cpu, 'Mathematics': Calculator, 'Science': FlaskConical, 'IELTS & PTE': Languages, 'Spoken Mastery': Mic };
                    const colorMap = { 'Coding': '#3B82F6', 'Robotics': '#EA580C', 'Mathematics': '#7C3AED', 'Science': '#059669', 'IELTS & PTE': '#E11D48', 'Spoken Mastery': '#0891B2' };
                    const imgMap = {
                      'Coding': 'https://images.pexels.com/photos/5665500/pexels-photo-5665500.jpeg?auto=compress&cs=tinysrgb&w=600',
                      'Robotics': 'https://images.pexels.com/photos/3913031/pexels-photo-3913031.jpeg?auto=compress&cs=tinysrgb&w=600',
                      'Mathematics': 'https://images.unsplash.com/photo-1758685733737-71f8945decf1?w=600&q=80',
                      'Science': 'https://images.unsplash.com/photo-1758685734153-132c8620c1bd?w=600&q=80',
                      'IELTS & PTE': 'https://images.pexels.com/photos/6929349/pexels-photo-6929349.jpeg?auto=compress&cs=tinysrgb&w=600',
                      'Spoken Mastery': 'https://images.pexels.com/photos/7647992/pexels-photo-7647992.jpeg?auto=compress&cs=tinysrgb&w=600',
                    };
                    const IconComp = iconMap[v.name] || BookOpen;
                    const color = colorMap[v.name] || '#3B82F6';
                    const bgImg = imgMap[v.name];
                    const programCount = (v.programs || []).length;
                    return (
                      <div key={v.id || v.name} onClick={() => setCurrSelectedVertical(v)}
                        className="relative rounded-2xl border border-slate-200/80 overflow-hidden cursor-pointer transition-all duration-200 group hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 hover:border-blue-300/60"
                        style={{ minHeight: '210px' }}
                        data-testid={`category-${v.name}`}>
                        {bgImg && <img src={bgImg} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-[#0F172A]/50 to-[#0F172A]/20 group-hover:from-[#0F172A]/95 group-hover:via-[#0F172A]/60 transition-all" />
                        <div className="relative z-10 p-6 flex flex-col justify-end h-full" style={{ minHeight: '210px' }}>
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 backdrop-blur-sm" style={{ backgroundColor: color + '30' }}>
                            <IconComp className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-1">{v.name}</h3>
                          {v.description && <p className="text-xs text-white/70 mb-3 leading-relaxed line-clamp-2">{v.description}</p>}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white">{programCount} program{programCount !== 1 ? 's' : ''}</span>
                            <ChevronRight className="w-4 h-4 text-white/60 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                          </div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteVertical(v.id); }} className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 p-1.5 text-white/70 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all backdrop-blur-sm" data-testid={`delete-category-${v.name}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── LEVEL 2: PROGRAMS ── */}
            {currSelectedVertical && !currSelectedProgram && (
              <div>
                <button onClick={() => { setCurrSelectedVertical(null); }} className="flex items-center gap-1 text-[#3B82F6] hover:text-[#2563EB] font-bold mb-4 text-sm"><ChevronRight className="w-4 h-4 rotate-180" /> Back to Categories</button>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]">{currSelectedVertical.name}</h2>
                    {currSelectedVertical.description && <p className="text-sm text-[#64748B]">{currSelectedVertical.description}</p>}
                  </div>
                  <Button onClick={() => setCurrAddProgramOpen(true)} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl" data-testid="add-program-btn"><Plus className="w-4 h-4 mr-1" /> Add Program</Button>
                </div>
                <Dialog open={currAddProgramOpen} onOpenChange={setCurrAddProgramOpen}>
                  <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Program</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <Input placeholder="Program name (e.g. Tech Sprout)" value={currNewProgram.name} onChange={e => setCurrNewProgram(p => ({...p, name: e.target.value}))} data-testid="program-name-input" />
                      <Input placeholder="Age range (e.g. 6-10)" value={currNewProgram.age_range} onChange={e => setCurrNewProgram(p => ({...p, age_range: e.target.value}))} />
                      <Button onClick={handleAddProgram} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Add Program</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                {/* Show modules directly if this is a module-based vertical */}
                {(currSelectedVertical.modules || []).length > 0 && !(currSelectedVertical.programs || []).length && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(currSelectedVertical.modules || []).map(mod => (
                      <div key={mod.id} className="nb-card nb-card-hover p-4" data-testid={`module-${mod.id}`}>
                        <h4 className="text-sm font-bold text-[#0F172A] mb-1">{mod.name}</h4>
                        <p className="text-xs text-[#64748B]">{mod.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {/* Program cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(currSelectedVertical.programs || []).map(prog => (
                    <div key={prog.id || prog.name} onClick={() => setCurrSelectedProgram(prog)}
                      className="relative nb-card nb-card-hover p-5 cursor-pointer transition-all group"
                      data-testid={`program-${prog.id || prog.name}`}>
                      <h3 className="text-base font-bold text-[#0F172A] mb-1">{prog.name}</h3>
                      {prog.age_range && <p className="text-xs text-[#3B82F6] font-bold mb-2">Ages {prog.age_range}</p>}
                      <p className="text-xs text-[#64748B]">{(prog.subjects || []).length} subject{(prog.subjects || []).length !== 1 ? 's' : ''}</p>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteProgram(prog.id); }} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── LEVEL 3: SUBJECTS ── */}
            {currSelectedVertical && currSelectedProgram && !currSelectedSubject && (
              <div>
                <button onClick={() => setCurrSelectedProgram(null)} className="flex items-center gap-1 text-[#3B82F6] hover:text-[#2563EB] font-bold mb-4 text-sm"><ChevronRight className="w-4 h-4 rotate-180" /> Back to {currSelectedVertical.name}</button>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]">{currSelectedProgram.name}</h2>
                    <p className="text-sm text-[#64748B]">{currSelectedVertical.name} &middot; Ages {currSelectedProgram.age_range}</p>
                  </div>
                  <Button onClick={() => setCurrAddSubjectOpen(true)} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl" data-testid="add-subject-btn"><Plus className="w-4 h-4 mr-1" /> Add Subject</Button>
                </div>
                <Dialog open={currAddSubjectOpen} onOpenChange={setCurrAddSubjectOpen}>
                  <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Subject</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <Input placeholder="Subject name (e.g. Scratch)" value={currNewSubjectName} onChange={e => setCurrNewSubjectName(e.target.value)} data-testid="subject-name-input" />
                      <Button onClick={handleAddSubject} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Add Subject</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(currSelectedProgram.subjects || []).map(subj => (
                    <div key={subj} onClick={() => handleSelectSubject(subj)}
                      className="relative nb-card nb-card-hover p-5 cursor-pointer transition-all group"
                      data-testid={`subject-${subj}`}>
                      <div className="w-10 h-10 rounded-lg bg-[#F0F7FF] flex items-center justify-center mb-3"><GraduationCap className="w-5 h-5 text-[#3B82F6]" /></div>
                      <h3 className="text-sm font-bold text-[#0F172A]">{subj}</h3>
                      <p className="text-xs text-[#64748B] mt-1">{currSelectedProgram.name}</p>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteSubject(subj); }} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── LEVEL 4-6: MODULES → TOPICS → PROJECTS ── */}
            {currSelectedSubject && (
              <div>
                <button onClick={() => { setCurrSelectedSubject(null); setCurrModules([]); }} className="flex items-center gap-1 text-[#3B82F6] hover:text-[#2563EB] font-bold mb-4 text-sm"><ChevronRight className="w-4 h-4 rotate-180" /> Back to {currSelectedProgram?.name || 'Subjects'}</button>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]">{currSelectedSubject}</h2>
                    <p className="text-xs text-[#64748B]">{currSelectedVertical?.name} &middot; {currSelectedProgram?.name}</p>
                  </div>
                  <Button onClick={() => setCurrAddModuleOpen(true)} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl" data-testid="add-module-btn"><Plus className="w-4 h-4 mr-1" /> Add Module</Button>
                </div>

                <Dialog open={currAddModuleOpen} onOpenChange={setCurrAddModuleOpen}>
                  <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Module</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <Input placeholder="Module name" value={currNewModuleName} onChange={e => setCurrNewModuleName(e.target.value)} data-testid="module-name-input" />
                      <Button onClick={handleAddModule} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Add Module</Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {currModules.length === 0 ? (
                  <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-12 text-center">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-[#64748B] text-sm">No modules yet. Add the first module to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currModules.map((mod, mi) => (
                      <div key={mod.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`module-${mod.id}`}>
                        <div onClick={() => setCurrExpandedModule(currExpandedModule === mod.id ? null : mod.id)} className="w-full p-4 flex items-center justify-between text-left hover:bg-[#F0F7FF] transition-colors cursor-pointer">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#EEF2FF] flex items-center justify-center text-[#4F46E5] font-bold text-sm">{mi + 1}</div>
                            <div><h3 className="font-bold text-[#0F172A]">{mod.module_name}</h3><p className="text-xs text-[#64748B]">{(mod.topics || []).length} topics</p></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); const n = prompt('Edit module name:', mod.module_name); if (n) api.put(`/admin/curriculum-templates/module/${mod.id}`, { module_name: n }).then(() => { fetchCurrModules(currSelectedSubject); toast.success('Updated'); }).catch(() => toast.error('Failed')); }} className="text-[#3B82F6] h-8 w-8 p-0"><Edit className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteModule(mod.id); }} className="text-red-500 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                            {currExpandedModule === mod.id ? <ChevronDown className="w-5 h-5 text-[#64748B]" /> : <ChevronRight className="w-5 h-5 text-[#64748B]" />}
                          </div>
                        </div>

                        {/* Topics inside module */}
                        {currExpandedModule === mod.id && (
                          <div className="px-4 pb-4 border-t border-slate-100">
                            <div className="space-y-2 mt-3">
                              {(mod.topics || []).map((t, ti) => (
                                <div key={t.id} className="bg-[#F8FAFC] rounded-lg border border-slate-200 overflow-hidden" data-testid={`topic-${t.id}`}>
                                  <div onClick={() => setCurrExpandedTopic(currExpandedTopic === t.id ? null : t.id)} className="w-full p-3 flex items-center justify-between text-left hover:bg-[#F0F7FF] transition-colors cursor-pointer">
                                    <div className="flex items-center gap-3">
                                      <span className="text-xs font-mono text-[#64748B] w-6">{ti + 1}</span>
                                      <div>
                                        <span className="font-medium text-[#0F172A] text-sm">{t.name}</span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.proficiency === 'Beginner' ? 'bg-green-100 text-green-700' : t.proficiency === 'Intermediate' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{t.proficiency}</span>
                                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.type === 'Concept' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{t.type}</span>
                                          {(t.projects || []).length > 0 && <span className="text-[10px] text-[#64748B]">{(t.projects || []).length} project{(t.projects || []).length !== 1 ? 's' : ''}</span>}
                                          {t.project_url && <a href={t.project_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-bold border border-blue-200 hover:bg-blue-100"><ExternalLink className="w-2.5 h-2.5 inline mr-0.5" />Project</a>}
                                          {t.presentation_url && <a href={t.presentation_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded text-[9px] font-bold border border-violet-200 hover:bg-violet-100">Slides</a>}
                                          {t.class_notes_url && <a href={t.class_notes_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[9px] font-bold border border-amber-200 hover:bg-amber-100">Notes</a>}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setCurrEditTopic({ moduleId: mod.id, ...t }); }} className="text-[#3B82F6] h-7 w-7 p-0"><Edit className="w-3 h-3" /></Button>
                                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteTopic(mod.id, t.id); }} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                                      {currExpandedTopic === t.id ? <ChevronDown className="w-4 h-4 text-[#64748B]" /> : <ChevronRight className="w-4 h-4 text-[#64748B]" />}
                                    </div>
                                  </div>

                                  {/* Projects inside topic */}
                                  {currExpandedTopic === t.id && (
                                    <div className="px-3 pb-3 border-t border-slate-200 bg-white">
                                      {/* Resource Links Editor */}
                                      <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid={`topic-resources-${t.id}`}>
                                        <p className="text-xs font-bold text-slate-700">Resource Links</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                          <div>
                                            <label className="text-[10px] font-semibold text-blue-600">Project URL <span className="text-[9px] text-slate-400 font-normal">(teacher + student)</span></label>
                                            <Input value={t.project_url || ''} onChange={e => handleUpdateTopic(mod.id, t.id, { project_url: e.target.value })} placeholder="https://scratch.mit.edu/..." className="h-7 text-xs mt-0.5" data-testid={`topic-project-url-${t.id}`} />
                                          </div>
                                          <div>
                                            <label className="text-[10px] font-semibold text-violet-600">Presentation URL <span className="text-[9px] text-slate-400 font-normal">(teacher + student)</span></label>
                                            <Input value={t.presentation_url || ''} onChange={e => handleUpdateTopic(mod.id, t.id, { presentation_url: e.target.value })} placeholder="https://docs.google.com/presentation/..." className="h-7 text-xs mt-0.5" data-testid={`topic-pres-url-${t.id}`} />
                                          </div>
                                          <div>
                                            <label className="text-[10px] font-semibold text-amber-600">Class Notes URL <span className="text-[9px] text-slate-400 font-normal">(student only)</span></label>
                                            <Input value={t.class_notes_url || ''} onChange={e => handleUpdateTopic(mod.id, t.id, { class_notes_url: e.target.value })} placeholder="https://docs.google.com/document/..." className="h-7 text-xs mt-0.5" data-testid={`topic-notes-url-${t.id}`} />
                                          </div>
                                        </div>
                                        <div>
                                          <label className="text-[10px] font-semibold text-rose-600">Teacher Script <span className="text-[9px] text-slate-400 font-normal">(teacher only — full lesson delivery script, HTML allowed)</span></label>
                                          <textarea
                                            value={t.teacher_script || ''}
                                            onChange={e => handleUpdateTopic(mod.id, t.id, { teacher_script: e.target.value })}
                                            placeholder="Step-by-step delivery script for the teacher. Include warm-up, activities, discussion questions, and wrap-up. HTML allowed."
                                            className="w-full min-h-[80px] text-xs mt-0.5 px-2 py-1.5 border border-slate-300 rounded-md focus:border-rose-400 focus:ring-1 focus:ring-rose-200"
                                            data-testid={`topic-teacher-script-${t.id}`}
                                          />
                                        </div>
                                      </div>

                                      {/* Document Library — uploadable files attached to this topic */}
                                      <div className="mt-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200 space-y-2" data-testid={`topic-documents-${t.id}`}>
                                        <div className="flex items-center justify-between">
                                          <p className="text-xs font-bold text-emerald-700 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Documents</p>
                                          {docUploadOpen === t.id ? null : (
                                            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => { setDocUploadOpen(t.id); fetchTopicDocs(mod.id, t.id); }} data-testid={`topic-doc-upload-btn-${t.id}`}>
                                              <Plus className="w-3 h-3 mr-1" /> Upload
                                            </Button>
                                          )}
                                        </div>
                                        {docUploadOpen === t.id && (
                                          <div className="space-y-2 bg-white rounded p-2 border border-emerald-200" data-testid={`topic-doc-upload-form-${t.id}`}>
                                            <input type="file" onChange={e => setDocUploadFile(e.target.files?.[0] || null)} className="text-xs" data-testid={`topic-doc-file-${t.id}`} />
                                            <Input value={docUploadLabel} onChange={e => setDocUploadLabel(e.target.value)} placeholder="Label (e.g. 'Slide deck v2')" className="h-7 text-xs" />
                                            <Input value={docUploadDesc} onChange={e => setDocUploadDesc(e.target.value)} placeholder="Short description" className="h-7 text-xs" />
                                            <div className="flex gap-2">
                                              <Button size="sm" disabled={docUploading} onClick={() => handleUploadDoc(mod.id, t.id)} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7" data-testid={`topic-doc-submit-${t.id}`}>{docUploading ? 'Uploading…' : 'Upload'}</Button>
                                              <Button size="sm" variant="outline" onClick={() => { setDocUploadOpen(null); setDocUploadFile(null); setDocUploadLabel(''); setDocUploadDesc(''); }} className="text-xs h-7">Cancel</Button>
                                            </div>
                                            <p className="text-[10px] text-slate-500">Allowed: PDF/Word/Excel/PowerPoint/Images. Max 25 MB.</p>
                                          </div>
                                        )}
                                        {!topicDocsMap[t.id] && (t.documents || []).length === 0 ? (
                                          <p className="text-[10px] text-slate-500 italic">No documents yet. Click Upload to add one.</p>
                                        ) : (
                                          <div className="space-y-1">
                                            {(topicDocsMap[t.id] || t.documents || []).map(d => (
                                              <div key={d.id} className="flex items-center gap-2 p-1.5 bg-white rounded border border-slate-200" data-testid={`topic-doc-${d.id}`}>
                                                <FileText className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                                <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-xs text-slate-900 hover:underline truncate" title={d.original_name}>
                                                  {d.label || d.original_name}
                                                </a>
                                                <span className="text-[9px] text-slate-400">{Math.round((d.size||0)/1024)} KB</span>
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600" onClick={() => handleEditDoc(mod.id, t.id, d.id, d)} data-testid={`topic-doc-edit-${d.id}`}><Edit className="w-3 h-3" /></Button>
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDeleteDoc(mod.id, t.id, d.id)} data-testid={`topic-doc-delete-${d.id}`}><Trash2 className="w-3 h-3" /></Button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      <div className="mt-2 space-y-2">
                                        {(t.projects || []).map((proj, pi) => (
                                          <div key={proj.id} className="flex items-start gap-3 p-3 bg-[#F0F7FF] rounded-lg border border-[#3B82F6]/10" data-testid={`project-${proj.id}`}>
                                            <span className="text-xs font-mono text-[#64748B] mt-1">{pi + 1}</span>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-bold text-[#0F172A]">{proj.name}</p>
                                              {proj.description && <p className="text-xs text-[#64748B] mt-0.5">{proj.description}</p>}
                                              {proj.project_link && <a href={proj.project_link} target="_blank" rel="noreferrer" className="text-xs text-[#3B82F6] hover:underline mt-0.5 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {proj.project_link}</a>}
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <Button variant="ghost" size="sm" onClick={() => setCurrEditProject({ moduleId: mod.id, topicId: t.id, ...proj })} className="text-[#3B82F6] h-6 w-6 p-0"><Edit className="w-3 h-3" /></Button>
                                              <Button variant="ghost" size="sm" onClick={() => handleDeleteProject(mod.id, t.id, proj.id)} className="text-red-500 h-6 w-6 p-0"><Trash2 className="w-3 h-3" /></Button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {currAddProjectOpen === t.id ? (
                                        <div className="mt-2 p-3 bg-[#FFFBEB] rounded-lg border border-amber-200/50 space-y-2">
                                          <Input placeholder="Project name" value={currNewProject.name} onChange={e => setCurrNewProject(p => ({...p, name: e.target.value}))} className="bg-white" data-testid="project-name-input" />
                                          <Input placeholder="Description" value={currNewProject.description} onChange={e => setCurrNewProject(p => ({...p, description: e.target.value}))} className="bg-white" />
                                          <Input placeholder="Project link (URL)" value={currNewProject.project_link} onChange={e => setCurrNewProject(p => ({...p, project_link: e.target.value}))} className="bg-white" />
                                          <div className="flex gap-2"><Button size="sm" onClick={() => handleAddProject(mod.id, t.id)} className="bg-[#3B82F6] hover:bg-[#2563EB]">Add Project</Button><Button size="sm" variant="outline" onClick={() => setCurrAddProjectOpen(null)}>Cancel</Button></div>
                                        </div>
                                      ) : (
                                        <Button variant="outline" size="sm" onClick={() => setCurrAddProjectOpen(t.id)} className="mt-2 text-xs" data-testid={`add-project-${t.id}`}><Plus className="w-3 h-3 mr-1" /> Add Project</Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {currAddTopicOpen === mod.id ? (
                              <div className="mt-3 p-4 bg-[#F0F7FF] rounded-lg border border-[#3B82F6]/15 space-y-3">
                                <Input placeholder="Topic name" value={currNewTopic.name} onChange={e => setCurrNewTopic(p => ({...p, name: e.target.value}))} className="bg-white" data-testid="topic-name-input" />
                                <Input placeholder="Description (optional)" value={currNewTopic.description} onChange={e => setCurrNewTopic(p => ({...p, description: e.target.value}))} className="bg-white" />
                                <div className="grid grid-cols-2 gap-2">
                                  <Select value={currNewTopic.proficiency} onValueChange={v => setCurrNewTopic(p => ({...p, proficiency: v}))}><SelectTrigger className="bg-white"><SelectValue placeholder="Proficiency" /></SelectTrigger><SelectContent><SelectItem value="Beginner">Beginner</SelectItem><SelectItem value="Intermediate">Intermediate</SelectItem><SelectItem value="Advanced">Advanced</SelectItem></SelectContent></Select>
                                  <Select value={currNewTopic.type} onValueChange={v => setCurrNewTopic(p => ({...p, type: v}))}><SelectTrigger className="bg-white"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="Concept">Concept</SelectItem><SelectItem value="Activity">Activity</SelectItem></SelectContent></Select>
                                </div>
                                <div className="flex gap-2"><Button size="sm" onClick={() => handleAddTopic(mod.id)} className="bg-[#3B82F6] hover:bg-[#2563EB]">Add Topic</Button><Button size="sm" variant="outline" onClick={() => setCurrAddTopicOpen(null)}>Cancel</Button></div>
                              </div>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => setCurrAddTopicOpen(mod.id)} className="mt-3" data-testid={`add-topic-${mod.id}`}><Plus className="w-3 h-3 mr-1" /> Add Topic</Button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Edit Topic Dialog */}
            {currEditTopic && (
              <Dialog open={!!currEditTopic} onOpenChange={() => setCurrEditTopic(null)}>
                <DialogContent className="bg-white"><DialogHeader><DialogTitle>Edit Topic</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-3">
                    <Input value={currEditTopic.name} onChange={e => setCurrEditTopic(p => ({...p, name: e.target.value}))} />
                    <Input placeholder="Description" value={currEditTopic.description || ''} onChange={e => setCurrEditTopic(p => ({...p, description: e.target.value}))} />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={currEditTopic.proficiency} onValueChange={v => setCurrEditTopic(p => ({...p, proficiency: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Beginner">Beginner</SelectItem><SelectItem value="Intermediate">Intermediate</SelectItem><SelectItem value="Advanced">Advanced</SelectItem></SelectContent></Select>
                      <Select value={currEditTopic.type} onValueChange={v => setCurrEditTopic(p => ({...p, type: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Concept">Concept</SelectItem><SelectItem value="Activity">Activity</SelectItem></SelectContent></Select>
                    </div>
                    <Button onClick={() => handleUpdateTopic(currEditTopic.moduleId, currEditTopic.id, { name: currEditTopic.name, description: currEditTopic.description, proficiency: currEditTopic.proficiency, type: currEditTopic.type })} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Edit Project Dialog */}
            {currEditProject && (
              <Dialog open={!!currEditProject} onOpenChange={() => setCurrEditProject(null)}>
                <DialogContent className="bg-white"><DialogHeader><DialogTitle>Edit Project</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-3">
                    <Input placeholder="Project name" value={currEditProject.name} onChange={e => setCurrEditProject(p => ({...p, name: e.target.value}))} />
                    <Input placeholder="Description" value={currEditProject.description || ''} onChange={e => setCurrEditProject(p => ({...p, description: e.target.value}))} />
                    <Input placeholder="Project link" value={currEditProject.project_link || ''} onChange={e => setCurrEditProject(p => ({...p, project_link: e.target.value}))} />
                    <Button onClick={() => handleUpdateProject(currEditProject.moduleId, currEditProject.topicId, currEditProject.id, { name: currEditProject.name, description: currEditProject.description, project_link: currEditProject.project_link })} className="w-full bg-[#3B82F6] hover:bg-[#2563EB]">Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* Approvals Section */}
        {activeSection === 'approvals' && (
          <ApprovalsSection
            approvalData={approvalData}
            approvalsTab={approvalsTab}
            setApprovalsTab={setApprovalsTab}
            teacherChangeReqs={teacherChangeReqs}
            teachers={teachers}
            handleLeaveAction={handleLeaveAction}
            handleApprovalRescheduleAction={handleApprovalRescheduleAction}
            handleApproveTeacherChange={handleApproveTeacherChange}
            handleRejectTeacherChange={handleRejectTeacherChange}
          />
        )}

        {/* Teacher Payouts Section */}
        {activeSection === 'teacher-payouts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div><h1 className="text-2xl font-bold text-slate-900">Teacher Payouts</h1><p className="text-sm text-slate-500 font-semibold">Earnings, payouts & deductions per teacher</p></div>
              <div className="flex gap-2">
                <Button onClick={() => setAddDeductionOpen(true)} variant="outline" className="bg-white border-red-400/40 text-red-600 font-bold hover:bg-red-50" data-testid="add-deduction-btn"><Plus className="w-4 h-4 mr-1" /> Add Deduction</Button>
                <Button onClick={() => setAddPayoutOpen(true)} className="bg-gray-50 border border-teal-400/40 text-teal-700 font-bold hover:bg-teal-50" data-testid="add-payout-btn"><Plus className="w-4 h-4 mr-1" /> Add Payout</Button>
              </div>
            </div>

            {/* Monthly Payouts (from Google Sheet) */}
            <div className="nb-card p-4 sm:p-6" data-testid="monthly-payouts-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Monthly Payouts</h2>
                  <p className="text-xs text-slate-500 font-semibold">Live data from Google Sheet · updates whenever you click Refresh</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={monthlyPayoutSel} onValueChange={setMonthlyPayoutSel}>
                    <SelectTrigger className="w-[180px] bg-white border border-slate-200" data-testid="monthly-payout-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[280px] overflow-auto">
                      {monthOptions.map(o => (
                        <SelectItem key={`${o.month}|${o.year}`} value={`${o.month}|${o.year}`} data-testid={`monthly-payout-option-${o.month}-${o.year}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchMonthlyPayouts} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50 font-bold" disabled={monthlyPayoutLoading} data-testid="monthly-payout-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${monthlyPayoutLoading ? 'animate-spin' : ''}`} />
                    {monthlyPayoutLoading ? 'Refreshing…' : 'Refresh'}
                  </Button>
                  <a
                    href="https://docs.google.com/spreadsheets/d/1y_2wPrYe0h1G6Oe85vF4MwO8cM2Om42g8oOJPJ4RHbs/edit?gid=1608561437"
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 font-semibold hover:underline flex items-center gap-1"
                    data-testid="monthly-payout-sheet-link"
                  >
                    <ExternalLink className="w-3 h-3" /> Open Sheet
                  </a>
                </div>
              </div>
              {monthlyPayoutData && (
                <div className="text-xs text-slate-600 font-semibold mb-2" data-testid="monthly-payout-summary">
                  {monthlyPayoutData.count} teacher{monthlyPayoutData.count === 1 ? '' : 's'} · Total credited: <span className="text-emerald-700 font-bold">₹{Number(monthlyPayoutData.total_credited || 0).toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Teacher</TableHead>
                      <TableHead className="text-xs">TID</TableHead>
                      <TableHead className="text-xs text-right">Classes</TableHead>
                      <TableHead className="text-xs text-right">US/CA</TableHead>
                      <TableHead className="text-xs text-right">India</TableHead>
                      <TableHead className="text-xs text-right">ROW</TableHead>
                      <TableHead className="text-xs text-right">Penalties</TableHead>
                      <TableHead className="text-xs text-right">Incentive</TableHead>
                      <TableHead className="text-xs text-right font-bold">Total (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(monthlyPayoutData?.rows || []).map((r, i) => (
                      <TableRow key={i} className="hover:bg-blue-50/50" data-testid={`monthly-payout-row-${r.teacher_id || r.teacher_name}`}>
                        <TableCell className="text-xs font-semibold text-slate-900">{r.teacher_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.teacher_id || <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-xs text-right">{r.total_classes ? Number(r.total_classes).toFixed(0) : '—'}</TableCell>
                        <TableCell className="text-xs text-right">{r.us_ca_amount ? `₹${Number(r.us_ca_amount).toLocaleString('en-IN')} (${Number(r.us_ca_count).toFixed(0)})` : '—'}</TableCell>
                        <TableCell className="text-xs text-right">{r.india_amount ? `₹${Number(r.india_amount).toLocaleString('en-IN')} (${Number(r.india_count).toFixed(0)})` : '—'}</TableCell>
                        <TableCell className="text-xs text-right">{r.row_amount ? `₹${Number(r.row_amount).toLocaleString('en-IN')} (${Number(r.row_count).toFixed(0)})` : '—'}</TableCell>
                        <TableCell className="text-xs text-right text-red-600">{r.penalties ? `-₹${Number(r.penalties).toLocaleString('en-IN')}` : '—'}</TableCell>
                        <TableCell className="text-xs text-right text-emerald-700">{r.incentive ? `+₹${Number(r.incentive).toLocaleString('en-IN')}` : '—'}</TableCell>
                        <TableCell className="text-xs text-right font-bold text-slate-900">₹{Number(r.total_credited || 0).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                    {(!monthlyPayoutData || (monthlyPayoutData.rows || []).length === 0) && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-xs text-center text-slate-400 py-6">
                          {monthlyPayoutLoading ? 'Loading…' : `No payouts recorded for ${monthlyPayoutSel.replace('|', ' ')}`}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Add Payout Dialog */}
            <Dialog open={addPayoutOpen} onOpenChange={setAddPayoutOpen}>
              <DialogContent className="bg-white"><DialogHeader><DialogTitle>Record Teacher Payout</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <Select value={payoutForm.teacher_id} onValueChange={v => setPayoutForm(p => ({...p, teacher_id: v}))}><SelectTrigger data-testid="payout-teacher"><SelectValue placeholder="Select Teacher" /></SelectTrigger><SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Amount</Label><Input type="number" value={payoutForm.amount} onChange={e => setPayoutForm(p => ({...p, amount: Number(e.target.value)}))} data-testid="payout-amount" /></div>
                    <div><Label>Period</Label><Input placeholder="e.g. March 2026" value={payoutForm.period} onChange={e => setPayoutForm(p => ({...p, period: e.target.value}))} /></div>
                  </div>
                  <div><Label>Notes</Label><Input value={payoutForm.notes} onChange={e => setPayoutForm(p => ({...p, notes: e.target.value}))} /></div>
                  <Button onClick={handleAddPayout} className="w-full bg-teal-600 hover:bg-teal-700">Record Payout</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Deduction Dialog */}
            <Dialog open={addDeductionOpen} onOpenChange={setAddDeductionOpen}>
              <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Deduction</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <Select value={deductionForm.teacher_id} onValueChange={v => setDeductionForm(p => ({...p, teacher_id: v}))}><SelectTrigger><SelectValue placeholder="Select Teacher" /></SelectTrigger><SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Amount (INR)</Label><Input type="number" value={deductionForm.amount} onChange={e => setDeductionForm(p => ({...p, amount: Number(e.target.value)}))} /></div>
                    <div><Label>Reason</Label><Input value={deductionForm.reason} onChange={e => setDeductionForm(p => ({...p, reason: e.target.value}))} /></div>
                  </div>
                  <div><Label>Notes</Label><Input value={deductionForm.notes} onChange={e => setDeductionForm(p => ({...p, notes: e.target.value}))} /></div>
                  <Button onClick={handleAddDeduction} className="w-full bg-red-600 hover:bg-red-700 text-white">Add Deduction</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Teacher Earnings Summary Cards */}
            {teacherPayouts.length === 0 ? (
              <div className="peach-card rounded-xl p-12 text-center"><CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-900 font-bold">No teacher data yet</p></div>
            ) : (
              <div className="space-y-4">
                {teacherPayouts.map(tp => (
                  <div key={tp.teacher_id} className="peach-card rounded-xl overflow-hidden" data-testid={`teacher-payout-${tp.teacher_id}`}>
                    <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-slate-500/10" onClick={() => setPayoutDetailTeacher(payoutDetailTeacher === tp.teacher_id ? null : tp.teacher_id)}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-lg">{(tp.teacher_name || '?')[0]}</div>
                        <div>
                          <h3 className="font-bold text-slate-900">{tp.teacher_name}</h3>
                          <p className="text-xs text-slate-500 font-semibold">{tp.email} &middot; {tp.total_classes} classes completed</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-right">
                        <div><div className="text-xs text-slate-500 font-semibold">Earned</div><div className="font-bold text-emerald-600">&#8377;{tp.total_earned?.toLocaleString()}</div></div>
                        <div><div className="text-xs text-slate-500 font-semibold">Paid</div><div className="font-bold text-blue-600">&#8377;{tp.total_paid?.toLocaleString()}</div></div>
                        <div><div className="text-xs text-slate-500 font-semibold">Deductions</div><div className="font-bold text-red-500">&#8377;{tp.total_deductions?.toLocaleString()}</div></div>
                        <div><div className="text-xs text-slate-500 font-semibold">Balance</div><div className={`font-bold ${tp.balance >= 0 ? 'text-teal-600' : 'text-red-600'}`}>&#8377;{tp.balance?.toLocaleString()}</div></div>
                        {payoutDetailTeacher === tp.teacher_id ? <ChevronDown className="w-5 h-5 text-slate-500 font-semibold" /> : <ChevronRight className="w-5 h-5 text-slate-500 font-semibold" />}
                      </div>
                    </div>
                    {payoutDetailTeacher === tp.teacher_id && (
                      <div className="px-5 pb-5 border-t border-black/10">
                        {/* Monthly Earnings Breakdown */}
                        {(tp.monthly_earnings || []).length > 0 && (
                          <div className="mt-4 mb-4">
                            <h4 className="text-xs font-bold text-emerald-700 mb-2 uppercase" data-testid={`monthly-earnings-title-${tp.teacher_id}`}>Monthly Earnings</h4>
                            <div className="bg-emerald-500/10 rounded-lg overflow-hidden">
                              <Table><TableHeader><TableRow className="bg-emerald-100"><TableHead className="text-xs font-semibold">Month</TableHead><TableHead className="text-xs font-semibold text-center">Classes</TableHead><TableHead className="text-xs font-semibold text-right">Earned (INR)</TableHead></TableRow></TableHeader><TableBody>
                                {tp.monthly_earnings.map((me, i) => (
                                  <TableRow key={me.month}><TableCell className="text-xs font-semibold">{me.month}</TableCell><TableCell className="text-xs text-center">{me.classes_completed}</TableCell><TableCell className="text-xs text-right font-bold text-emerald-600">&#8377;{me.paid_class_earnings?.toLocaleString()}</TableCell></TableRow>
                                ))}
                              </TableBody></Table>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          {/* Payouts */}
                          <div>
                            <h4 className="text-xs font-bold text-slate-700 font-semibold mb-2 uppercase">Payouts Made</h4>
                            {(tp.payouts || []).length === 0 ? <p className="text-xs text-slate-500 font-semibold">No payouts yet</p> : (
                              <div className="space-y-1">{tp.payouts.map(p => (
                                <div key={p.id} className="flex items-center justify-between text-sm bg-slate-500/10 rounded-lg px-3 py-2">
                                  <span className="text-slate-700 font-semibold">&#8377;{p.amount?.toLocaleString()}</span>
                                  <span className="text-xs text-slate-500 font-semibold">{p.period || new Date(p.created_at).toLocaleDateString()}</span>
                                </div>
                              ))}</div>
                            )}
                          </div>
                          {/* Deductions */}
                          <div>
                            <h4 className="text-xs font-bold text-red-600 mb-2 uppercase">Deductions</h4>
                            {(tp.deductions || []).length === 0 ? <p className="text-xs text-slate-500 font-semibold">No deductions</p> : (
                              <div className="space-y-1">{tp.deductions.map(d => (
                                <div key={d.id} className="flex items-center justify-between text-sm bg-red-500/10 rounded-lg px-3 py-2">
                                  <span className="text-red-700">-&#8377;{d.amount?.toLocaleString()} <span className="text-xs text-red-400">{d.reason}</span></span>
                                  <span className="text-xs text-slate-500 font-semibold">{new Date(d.created_at).toLocaleDateString()}</span>
                                </div>
                              ))}</div>
                            )}
                          </div>
                        </div>
                        {/* Class Details */}
                        {(tp.class_details || []).length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-xs font-bold text-slate-700 font-semibold mb-2 uppercase">Class Completion Details</h4>
                            <div className="bg-slate-500/10 rounded-lg overflow-hidden"><Table><TableHeader><TableRow className="bg-slate-100"><TableHead className="text-xs">Student</TableHead><TableHead className="text-xs">Amount</TableHead><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Topic</TableHead></TableRow></TableHeader><TableBody>
                              {tp.class_details.map((cd, i) => (
                                <TableRow key={`${cd.date}-${cd.student_name}-${i}`}><TableCell className="text-xs">{cd.student_name}</TableCell><TableCell className="text-xs font-medium text-emerald-600">&#8377;{cd.amount}</TableCell><TableCell className="text-xs">{cd.date}</TableCell><TableCell className="text-xs text-slate-500 font-semibold">{cd.topic}</TableCell></TableRow>
                              ))}
                            </TableBody></Table></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Packages Section - removed from sidebar but keeping render for safety */}
        {activeSection === 'packages' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900">Packages</h1>
              <p className="text-slate-500 font-semibold">Class packages available for purchase</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allPackages.map((pkg) => (
                <div key={pkg.id} className="glass-card rounded-xl p-6 shadow-sm border-2 border-blue-100">
                  <h3 className="text-xl font-bold text-black mb-2">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">
                    ${pkg.price_monthly}<span className="text-sm text-slate-500 font-semibold font-normal">/month</span>
                  </div>
                  <div className="space-y-2 text-slate-500 font-semibold">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-blue-500" />
                      <span>{pkg.classes} classes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span>{pkg.hours} hours total</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 font-semibold mt-4">{pkg.description}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Schedules Section */}
        {activeSection === 'schedules' && (
          <SchedulesSection
            students={students} teachers={teachers}
            schedules={schedules} scheduledClasses={scheduledClasses}
            rescheduleRequests={rescheduleRequests}
            createScheduleOpen={createScheduleOpen} setCreateScheduleOpen={setCreateScheduleOpen}
            scheduleType={scheduleType} setScheduleType={setScheduleType}
            newSchedule={newSchedule} setNewSchedule={setNewSchedule}
            savingSchedule={savingSchedule} handleCreateSchedule={handleCreateSchedule}
            toggleDay={toggleDay}
            handleRescheduleAction={handleRescheduleAction}
            handleDeleteSchedule={handleDeleteSchedule}
            handleMarkComplete={handleMarkComplete}
            fetchScheduleData={fetchScheduleData}
            getCurrentTimeInTZ={getCurrentTimeInTZ}
            WEEKDAYS={WEEKDAYS}
          />
        )}

        {/* Class Packages Section */}
        {activeSection === 'packages' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Class Packages</h1>
                <p className="text-slate-500 font-semibold">Manage enrollment packages and purchase requests</p>
              </div>
              <div className="flex gap-3">
              <Button onClick={() => setAssignPackageOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="assign-package-btn">
                <CheckCircle className="w-4 h-4 mr-2" /> Assign Package & Mark Paid
              </Button>
              <Dialog open={createPackageOpen} onOpenChange={(open) => { setCreatePackageOpen(open); if (!open) { setEditingPackage(null); setPackageForm({ name: '', num_classes: 0, price_inr: 0, price_usd: 0, description: '', is_active: true }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-package-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Package
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-blue-200 shadow-xl max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-blue-700 font-bold">{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
                    <DialogDescription>Define class package with pricing</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Package Name</Label>
                      <Input value={packageForm.name} onChange={e => setPackageForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Starter - 8 Classes" className="mt-1" data-testid="package-name" />
                    </div>
                    <div>
                      <Label>Number of Classes</Label>
                      <Input type="number" min="1" value={packageForm.num_classes} onChange={e => setPackageForm(p => ({...p, num_classes: parseInt(e.target.value) || 0}))} className="mt-1" data-testid="package-classes" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Amount (INR) {packageForm.price_inr > 0 && <span className="text-emerald-600 text-xs ml-1">set</span>}</Label>
                        <Input type="number" min="0" step="0.01" value={packageForm.price_inr || ''} onChange={e => setPackageForm(p => ({...p, price_inr: parseFloat(e.target.value) || 0}))} placeholder="e.g. 5000" className="mt-1" data-testid="package-price-inr" />
                      </div>
                      <div>
                        <Label>Amount (USD) {packageForm.price_usd > 0 && <span className="text-emerald-600 text-xs ml-1">set</span>}</Label>
                        <Input type="number" min="0" step="0.01" value={packageForm.price_usd || ''} onChange={e => setPackageForm(p => ({...p, price_usd: parseFloat(e.target.value) || 0}))} placeholder="e.g. 60" className="mt-1" data-testid="package-price-usd" />
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 font-semibold">Enter amount in at least one currency (INR or USD)</p>
                    <div>
                      <Label>Description</Label>
                      <Input value={packageForm.description} onChange={e => setPackageForm(p => ({...p, description: e.target.value}))} placeholder="Brief description" className="mt-1" data-testid="package-desc" />
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={packageForm.is_active} onChange={e => setPackageForm(p => ({...p, is_active: e.target.checked}))} className="rounded" />
                      <Label className="mb-0">Active (visible to parents)</Label>
                    </div>
                    <Button onClick={handleSavePackage} disabled={savingPackage} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-package-btn">
                      {savingPackage ? 'Saving...' : (editingPackage ? 'Update Package' : 'Create Package')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {/* Assign Package Dialog */}
            <Dialog open={assignPackageOpen} onOpenChange={setAssignPackageOpen}>
              <DialogContent className="bg-white border-2 border-emerald-400/30 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-emerald-900">Assign Package & Mark as Paid</DialogTitle>
                  <DialogDescription>Directly assign a class package to a student and record the payment</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Student</Label>
                    <Select value={assignData.student_id} onValueChange={v => setAssignData(p => ({...p, student_id: v}))}>
                      <SelectTrigger data-testid="assign-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Package</Label>
                    <Select value={assignData.package_id} onValueChange={v => { const pkg = classPackages.find(p => p.id === v); setAssignData(p => ({...p, package_id: v, payment_received: pkg ? (p.currency === 'INR' ? pkg.price_inr : pkg.price_usd) : 0})); }}>
                      <SelectTrigger data-testid="assign-package"><SelectValue placeholder="Select package" /></SelectTrigger>
                      <SelectContent>{classPackages.filter(p => p.is_active).map(pkg => <SelectItem key={pkg.id} value={pkg.id}>{pkg.name} — {pkg.num_classes} classes</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Currency</Label>
                      <Select value={assignData.currency} onValueChange={v => { const pkg = classPackages.find(p => p.id === assignData.package_id); setAssignData(p => ({...p, currency: v, payment_received: pkg ? (v === 'INR' ? pkg.price_inr : pkg.price_usd) : p.payment_received})); }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="INR">INR (₹)</SelectItem><SelectItem value="USD">USD ($)</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount Received ({assignData.currency === 'INR' ? '₹' : '$'})</Label>
                      <Input type="number" min="0" step="0.01" value={assignData.payment_received} onChange={e => setAssignData(p => ({...p, payment_received: parseFloat(e.target.value) || 0}))} data-testid="assign-amount" />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block">Payment Method</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'razorpay_card', label: 'Card (Razorpay)' },
                        { id: 'razorpay_upi', label: 'UPI (Razorpay)' },
                        { id: 'paypal', label: 'PayPal' },
                        { id: 'bank_transfer', label: 'Bank Transfer' },
                      ].map(m => (
                        <button key={m.id} onClick={() => setAssignData(p => ({...p, payment_method: m.id}))}
                          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${assignData.payment_method === m.id ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-400 font-semibold border-slate-200 hover:border-emerald-400'}`}
                          data-testid={`assign-method-${m.id}`}>
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {assignData.package_id && (
                    <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-400/30 text-center">
                      <p className="text-sm text-emerald-700 font-medium">{classPackages.find(p => p.id === assignData.package_id)?.name}</p>
                      <p className="text-2xl font-bold text-emerald-800 mt-1">{assignData.currency === 'INR' ? '₹' : '$'}{assignData.payment_received.toLocaleString()}</p>
                      <p className="text-xs text-emerald-600">{classPackages.find(p => p.id === assignData.package_id)?.num_classes} classes will be added</p>
                    </div>
                  )}
                  <Button onClick={handleAssignPackage} disabled={assigningPackage} className="w-full bg-emerald-600 hover:bg-emerald-700 text-slate-900 font-bold" data-testid="confirm-assign-btn">
                    {assigningPackage ? 'Processing...' : 'Confirm Payment & Assign Classes'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Purchase Requests */}
            {purchaseRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="bg-amber-500/10 rounded-xl border-2 border-amber-400/30 p-5 mb-8">
                <h2 className="font-bold text-black mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-600" /> Pending Purchase Requests</h2>
                <div className="space-y-3">
                  {purchaseRequests.filter(r => r.status === 'pending').map(req => (
                    <div key={req.id} className="glass-card rounded-lg p-4 border flex items-center justify-between" data-testid={`purchase-req-${req.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{req.parent_name}</p>
                          <span className="text-xs text-slate-500 font-semibold">for</span>
                          <p className="font-medium text-sm text-blue-600 font-bold">{req.student_name}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-500 font-semibold">{req.package_name} ({req.num_classes} classes)</span>
                          <span className="text-xs font-bold text-emerald-600">{req.currency === 'INR' ? '₹' : '$'}{req.amount}</span>
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-500 font-semibold">{PAYMENT_METHOD_LABELS[req.payment_method] || req.payment_method}</span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-semibold mt-1">{new Date(req.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handlePurchaseAction(req.id, 'confirm')} data-testid={`confirm-purchase-${req.id}`}><CheckCircle className="w-3 h-3 mr-1" /> Confirm</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-400/30 text-xs" onClick={() => handlePurchaseAction(req.id, 'reject')} data-testid={`reject-purchase-${req.id}`}><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Packages Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {classPackages.map(pkg => (
                <div key={pkg.id} className={`bg-white rounded-xl border-2 p-6 transition-all ${pkg.is_active ? 'border-blue-400/30 hover:border-blue-400' : 'border-slate-200 opacity-60'}`} data-testid={`package-card-${pkg.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{pkg.name}</h3>
                      {pkg.description && <p className="text-xs text-slate-500 font-semibold mt-0.5">{pkg.description}</p>}
                    </div>
                    {!pkg.is_active && <span className="px-2 py-0.5 rounded-full bg-gray-200 text-slate-500 font-semibold text-[10px] font-bold">INACTIVE</span>}
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-4 text-center mb-4">
                    <div className="text-3xl font-black text-blue-600 font-bold">{pkg.num_classes}</div>
                    <div className="text-xs text-blue-500 font-medium">Classes</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    {pkg.price_inr > 0 && (
                      <div className="text-center flex-1">
                        <div className="text-xl font-bold text-slate-900">₹{pkg.price_inr?.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500 font-semibold">INR</div>
                      </div>
                    )}
                    {pkg.price_inr > 0 && pkg.price_usd > 0 && <div className="w-px h-8 bg-gray-200"></div>}
                    {pkg.price_usd > 0 && (
                      <div className="text-center flex-1">
                        <div className="text-xl font-bold text-slate-900">${pkg.price_usd}</div>
                        <div className="text-[10px] text-slate-500 font-semibold">USD</div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openEditPackage(pkg)} data-testid={`edit-pkg-${pkg.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" className="text-red-500 border-red-400/30 text-xs" onClick={() => handleDeletePackage(pkg.id)} data-testid={`delete-pkg-${pkg.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
              {classPackages.length === 0 && (
                <div className="col-span-3 text-center py-12 text-slate-500 font-semibold">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No packages created yet</p>
                  <p className="text-xs mt-1">Create your first class package above</p>
                </div>
              )}
            </div>

            {/* Purchase History */}
            {purchaseRequests.filter(r => r.status !== 'pending').length > 0 && (
              <div className="glass-card rounded-xl overflow-x-auto">
                <div className="px-6 py-4 border-b bg-blue-500/10"><h2 className="font-bold text-blue-700 font-bold">Purchase History</h2></div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Parent</TableHead>
                      <TableHead className="font-semibold">Student</TableHead>
                      <TableHead className="font-semibold">Package</TableHead>
                      <TableHead className="font-semibold">Amount</TableHead>
                      <TableHead className="font-semibold">Method</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseRequests.filter(r => r.status !== 'pending').map(req => (
                      <TableRow key={req.id}>
                        <TableCell className="text-xs">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{req.parent_name}</TableCell>
                        <TableCell className="font-medium">{req.student_name}</TableCell>
                        <TableCell>{req.package_name}</TableCell>
                        <TableCell className="font-bold">{req.currency === 'INR' ? '₹' : '$'}{req.amount}</TableCell>
                        <TableCell><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{PAYMENT_METHOD_LABELS[req.payment_method] || req.payment_method}</span></TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${req.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{req.status}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* Transactions Section */}
        {activeSection === 'announcements' && (
          <AnnouncementsManager />
        )}

        {activeSection === 'messages' && (
          <div className="max-w-5xl">
            <ChatPanel currentUser={user} />
          </div>
        )}

        {activeSection === 'transactions' && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Transactions</h1>
                <p className="text-slate-500 font-semibold text-sm">All revenues, payouts, and deductions</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-2">
                  <span className="text-emerald-600 font-medium">Revenue: </span>
                  <span className="font-bold text-emerald-700">
                    {'\u20B9'}{transactions.filter(t => t.type === 'revenue').reduce((s, t) => s + (t.amount_inr || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="bg-red-500/10 border border-red-400/30 rounded-xl px-4 py-2">
                  <span className="text-red-600 font-medium">Payouts: </span>
                  <span className="font-bold text-red-700">
                    {'\u20B9'}{transactions.filter(t => t.type === 'payout').reduce((s, t) => s + (t.amount_inr || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl px-4 py-2">
                  <span className="text-amber-600 font-medium">Deductions: </span>
                  <span className="font-bold text-amber-700">
                    {'\u20B9'}{transactions.filter(t => t.type === 'deduction').reduce((s, t) => s + (t.amount_inr || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-500/10">
                    <TableHead className="font-semibold text-slate-700 font-semibold">Type</TableHead>
                    <TableHead className="font-semibold text-slate-700 font-semibold">Description</TableHead>
                    <TableHead className="font-semibold text-slate-700 font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold text-slate-700 font-semibold">Amount (INR)</TableHead>
                    <TableHead className="font-semibold text-slate-700 font-semibold">Date</TableHead>
                    <TableHead className="font-semibold text-slate-700 font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 font-semibold py-8">No transactions</TableCell></TableRow>
                  ) : transactions.map((tx) => (
                    <TableRow key={tx.id} className="hover:bg-slate-500/10" data-testid={`tx-row-${tx.id}`}>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          tx.type === 'revenue' ? 'bg-emerald-100 text-emerald-700' :
                          tx.type === 'payout' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{tx.type}</span>
                      </TableCell>
                      <TableCell>
                        {editingTransaction === tx.id ? (
                          <Input value={txEditForm.notes || ''} onChange={e => setTxEditForm(p => ({...p, notes: e.target.value}))} className="h-8 text-xs" placeholder="Notes" />
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-slate-800 font-semibold">{tx.description}</p>
                            {tx.notes && <p className="text-xs text-slate-500 font-semibold">{tx.notes}</p>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingTransaction === tx.id ? (
                          <div className="flex items-center gap-1">
                            <Input type="number" value={txEditForm.amount || 0} onChange={e => setTxEditForm(p => ({...p, amount: parseFloat(e.target.value) || 0}))} className="h-8 text-xs w-24" />
                            <select value={txEditForm.currency || 'INR'} onChange={e => setTxEditForm(p => ({...p, currency: e.target.value}))} className="h-8 text-xs border rounded px-1">
                              <option value="INR">INR</option><option value="USD">USD</option><option value="CAD">CAD</option>
                            </select>
                          </div>
                        ) : (
                          <span className={`font-bold ${tx.type === 'revenue' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {tx.type === 'payout' || tx.type === 'deduction' ? '-' : '+'}{tx.amount} {tx.currency}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 font-semibold">{'\u20B9'}{tx.amount_inr?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-500 font-semibold">{tx.date?.split('T')[0] || 'N/A'}</TableCell>
                      <TableCell>
                        {tx.editable && (
                          <div className="flex items-center gap-1">
                            {editingTransaction === tx.id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleEditTransaction(tx)} className="text-emerald-600 h-7 w-7 p-0"><Check className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingTransaction(null)} className="text-slate-500 font-semibold h-7 w-7 p-0"><X className="w-3.5 h-3.5" /></Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => {
                                  setEditingTransaction(tx.id);
                                  setTxEditForm({ amount: tx.amount, currency: tx.currency, notes: tx.notes || '' });
                                }} className="text-blue-600 h-7 w-7 p-0" data-testid={`edit-tx-${tx.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteTransaction(tx)} className="text-red-500 h-7 w-7 p-0" data-testid={`delete-tx-${tx.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {/* Teacher Issues Section */}
        {activeSection === 'teacher-issues' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900" data-testid="teacher-issues-title">Teacher Issues</h1>
              <p className="text-slate-500 font-semibold">Issues reported by teachers regarding earnings or other concerns</p>
            </div>
            {teacherIssues.length === 0 ? (
              <div className="text-center py-16 text-slate-500 font-semibold">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-lg">No issues reported</p>
              </div>
            ) : (
              <div className="space-y-4">
                {teacherIssues.map(issue => (
                  <div key={issue.id} className={`bg-white rounded-xl border p-5 ${issue.status === 'open' ? 'border-red-400/30' : 'border-green-400/30'}`} data-testid={`issue-${issue.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-slate-900">{issue.teacher_name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${issue.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{issue.status?.toUpperCase()}</span>
                          {issue.type && <span className="px-2 py-0.5 bg-slate-100 text-slate-500 font-semibold rounded text-xs">{issue.type}</span>}
                          {issue.month && <span className="text-xs text-slate-500 font-semibold">Month: {issue.month}</span>}
                        </div>
                        {issue.subject && <p className="font-semibold text-slate-700 font-semibold mt-1">{issue.subject}</p>}
                        <p className="text-sm text-slate-500 font-semibold mt-1">{issue.description}</p>
                        <p className="text-xs text-slate-500 font-semibold mt-2">{new Date(issue.created_at).toLocaleString()}</p>
                      </div>
                      {issue.status === 'open' && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-slate-900 ml-4" onClick={() => handleResolveIssue(issue.id)} data-testid={`resolve-issue-${issue.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        </div>
      </main>
      <AdminCurriculumProgression open={curriculumMgrOpen} onClose={() => setCurriculumMgrOpen(false)} />
      <OlympiadBuilder open={olympiadBuilderOpen} onClose={() => setOlympiadBuilderOpen(false)} />
      <StudentProfileCard
        open={!!profileCardStudentId}
        studentId={profileCardStudentId}
        onClose={() => setProfileCardStudentId(null)}
        onUpdated={() => { fetchStudents(); }}
      />
    </div>
  );
};

export default AdminDashboard;
